import {
    CalculatorOutlined,
    CarOutlined,
    CheckCircleFilled,
    CloseOutlined,
    ControlOutlined,
    DeleteOutlined,
    EditOutlined,
    EnvironmentOutlined,
    FileDoneOutlined,
    FileSearchOutlined,
    FileTextOutlined,
    InfoCircleOutlined,
    NumberOutlined,
    PercentageOutlined,
    PlusOutlined,
    PrinterOutlined,
    ReloadOutlined,
    RightOutlined,
    SaveOutlined,
    SearchOutlined,
    ShopOutlined,
    ShoppingOutlined,
    TagOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";
import {
    AutoComplete,
    Button,
    Checkbox,
    DatePicker,
    Input,
    Modal,
    Popconfirm,
    Select,
    Table,
    Tag,
    Tooltip,
    message,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DashboardTableHeaderStyles from ".././Pages/DashboardTableHeaderStyles";
import {
    FIELDS,
    SAVE_EACH_NEW_ITEM,
    apiError,
    buildCurrentStockPayload,
    buildDesignCode,
    buildPurchasePayload,
    calcPurchaseRow,
    calcTotals,
    clean,
    deleteCurrentStock,
    deletePurchInvoice,
    fetchCounterConfig,
    fetchMaxSeq,
    fetchNextEntryNo,
    fetchPurchaseList,
    fetchSuppliersByArea,
    getFyearCode,
    loadMaster,
    loadSavedInvoice,
    money,
    newRowKeys,
    num,
    pick,
    resolveGstPer,
    saveCurrentStock,
    savePurchInvoice,
    supplierKey,
    toOptions,
    updateCurrentStock,
    updatePurchInvoice,
    buildCodeMap,
    buildPriceCode,
} from "./Purchaseinvoicedata";

/* ════════════════════════════════════════════════════════════════
   ENTER-KEY FLOW (useRef)
   ────────────────────────────────────────────────────────────────
   ORDER      = the exact order the cursor travels when Enter is pressed.
                Read-only boxes (Design Code, Amount, Dir Price, Gross Amt,
                Dis Amt, Offer Price) are not in it – they are calculated.
   REQ        = mandatory fields. Enter does NOT move on until they are filled.
                Fields that are in ORDER but not in REQ are optional: just
                press Enter to skip them.
   ITEM_REQ_ORDER = the mandatory item fields, checked again by Add Item.
   To make a field mandatory / optional, change ONLY REQ.
   To change the travel order, change ONLY ORDER.

   Dis (%) is OPTIONAL: it is in ORDER (cursor stops there) but not in REQ /
   ITEM_REQ_ORDER. When left empty it is saved as 0.
   ════════════════════════════════════════════════════════════════ */

const ORDER = [
    "purchaseType",
    "supplierBrowse",
    "invoiceNo",
    "invoiceDate",
    "stockRecDate",
    "dueDays",
    "marginPer",
    "brandCode",
    "brandName",
    "itemCategory",
    "itemName",
    "hsn",
    "size",
    "colour",
    "fitting",
    "offer",
    "qty",
    "free",
    "grossDp",
    "plusPer",
    "purDisPer",
    // "gstPer",
    "wPer",
    "salePer",
    "custPrice",
    "disPer",
    "addItem",
];

const nonEmpty = (v) => String(v ?? "").trim() !== "";

// key → [label shown in the warning, rule(state) → true when filled]
const REQ = {
    invoiceNo: ["Invoice No", (s) => nonEmpty(s.header.invoiceNo)],
    brandName: ["Brand Name", (s) => nonEmpty(s.item.brandName)],
    itemCategory: ["Item Category", (s) => nonEmpty(s.item.itemCategory)],
    itemName: ["Item Name", (s) => nonEmpty(s.item.itemName)],
    hsn: ["HSN Code", (s) => nonEmpty(s.item.hsn)],
    size: ["Size", (s) => nonEmpty(s.item.size)],
    colour: ["Color", (s) => nonEmpty(s.item.colour)],
    fitting: ["Fashion", (s) => nonEmpty(s.item.fitting)],
    qty: ["Qty", (s) => num(s.item.qty) > 0],
    grossDp: ["Gross DP", (s) => num(s.item.grossDp) > 0],
    salePer: ["Sale (%)", (s) => s.showSalePer !== ""],
    custPrice: ["Cust Price", (s) => s.showCustPrice !== ""],
    // disPer is NOT here → Dis (%) is optional (Enter skips it, empty is saved as 0)
};

const ITEM_REQ_ORDER = [
    "brandName",
    "itemCategory",
    "itemName",
    "hsn",
    "size",
    "colour",
    "fitting",
    "qty",
    "grossDp",
    "salePer",
    "custPrice",
    // "disPer" removed → optional
];

// first mandatory item field that is still empty (or null when all are filled)
const findMissingItemField = (s) =>
    ITEM_REQ_ORDER.find((k) => !REQ[k][1](s)) || null;

// the fields that are locked once an invoice is saved / opened from the list
const LOCKED_KEYS = ["purchaseType", "supplierBrowse", "invoiceNo"];

/* ════════════════════════════════════════════════════════════════
   STYLES
   ════════════════════════════════════════════════════════════════ */

const S = {
    label: { fontSize: "0.84rem", fontWeight: 600, color: "#475569" },
    title: {
        fontSize: "0.74rem",
        fontWeight: 800,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
    },
    readonly: {
        background: "#f1f5f9",
        fontWeight: 700,
        color: "#334155",
        textAlign: "right",
        fontSize: 14,
    },
};

/* ── ONE place for every card heading-icon colour ── */
const ACCENT = {
    supplier: "linear-gradient(135deg,#2563eb,#4f46e5)", // blue (was green/teal)
    invoice: "linear-gradient(135deg,#f43f5e,#f97316)", // rose → orange
    item: "linear-gradient(135deg,#7c3aed,#c026d3)", // violet → fuchsia
    qty: "linear-gradient(135deg,#0ea5e9,#0369a1)", // sky
    purchase: "linear-gradient(135deg,#f59e0b,#d97706)", // amber
    wholesale: "linear-gradient(135deg,#ec4899,#be185d)", // pink
    sale: "linear-gradient(135deg,#6366f1,#4338ca)", // indigo
    added: "linear-gradient(135deg,#475569,#0f172a)", // slate
    amount: "linear-gradient(135deg,#fb923c,#ea580c)", // orange
};

// ticket shape for the Entry No block: two round notches cut out of the sides
const TICKET_MASK =
    "radial-gradient(circle 7px at 0 50%, transparent 97%, #000) left / 52% 100% no-repeat, radial-gradient(circle 7px at 100% 50%, transparent 97%, #000) right / 52% 100% no-repeat";

// avatar colours for the supplier browse table
const AVATAR_COLORS = ["#2563eb", "#4f46e5", "#d97706", "#be185d", "#0284c7", "#7c3aed"];
const avatarColor = (name = "") =>
    AVATAR_COLORS[
    [...String(name)].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length
    ];

// sale price roundoff choices (shown as chips inside the dialog)
const ROUND_OPTS = [
    { value: "GENERAL", label: "General" },
    { value: "5", label: "5 Rupees" },
    { value: "9", label: "9 Rupees" },
    { value: "0", label: "0 Rupees" },
];

/* ONE height for every input / select / autocomplete / date picker:
   change --pi-h below and the whole screen follows.
   --pi-bg = colour behind the white cards. The round notches of the
   "ticket" cards use the same colour so they look cut out. */
const TABLE_CSS = `
.pi-root, .pi-modal { --pi-h: 36px; }
.pi-root { --pi-bg: #e9eef5; }
.pi-root .pi-foot { --pi-h: 29px; }

/* ---- every control = same height + same font ---- */
.pi-root input.ant-input,
.pi-modal input.ant-input,
.pi-root .ant-input-affix-wrapper,
.pi-modal .ant-input-affix-wrapper,
.pi-root .ant-picker,
.pi-modal .ant-picker,
.pi-root .ant-select .ant-select-selector,
.pi-modal .ant-select .ant-select-selector {
    height: var(--pi-h) !important;
    min-height: var(--pi-h) !important;
    box-sizing: border-box !important;
    font-size: 14px !important;
}
.pi-root .ant-input-affix-wrapper input.ant-input,
.pi-modal .ant-input-affix-wrapper input.ant-input {
    height: auto !important;
    min-height: 0 !important;
}
.pi-root .ant-picker input,
.pi-modal .ant-picker input { font-size: 14px !important; }
.pi-root .ant-select .ant-select-selector,
.pi-modal .ant-select .ant-select-selector { align-items: center !important; }
.pi-root .ant-select .ant-select-selection-item,
.pi-root .ant-select .ant-select-selection-placeholder,
.pi-modal .ant-select .ant-select-selection-item,
.pi-modal .ant-select .ant-select-selection-placeholder {
    line-height: calc(var(--pi-h) - 2px) !important;
}
.pi-root .ant-select .ant-select-selection-search-input,
.pi-modal .ant-select .ant-select-selection-search-input {
    height: calc(var(--pi-h) - 2px) !important;
}
.pi-root textarea.ant-input,
.pi-modal textarea.ant-input { font-size: 14px !important; height: auto !important; }

/* locked (disabled) inputs stay readable instead of fading away */
.pi-root input.ant-input[disabled],
.pi-root .ant-input-disabled {
    color: #334155 !important;
    -webkit-text-fill-color: #334155 !important;
    background: #f1f5f9 !important;
    font-weight: 700;
}

/* narrow value fields: less side padding so numbers fit */
.pi-root .pi-tight input.ant-input { padding-left: 6px !important; padding-right: 6px !important; }

/* ---- footer totals ---- */
.pi-root .pi-foot input.ant-input { font-size: 15px !important; font-weight: 700 !important; }

/* ════════ PURCHASE-TYPE GATE ════════
   Every block (except the top strip) sits inside a <fieldset class="pi-gate">.
   While no purchase type is chosen the fieldset is disabled: the browser itself
   blocks typing / tabbing / clicking, and we fade + grey the blocks. */
.pi-gate {
    border: 0;
    margin: 0;
    padding: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex-shrink: 0;
    transition: opacity 0.25s, filter 0.25s;
}
.pi-gate:disabled {
    opacity: 0.45;
    filter: grayscale(0.4);
    pointer-events: none;
    user-select: none;
}
@keyframes pi-hint-bob {
    0%, 100% { transform: translate(-50%, -50%); }
    50%      { transform: translate(-50%, calc(-50% - 5px)); }
}
.pi-gate-hint {
    position: absolute;
    left: 50%;
    top: 48%;
    z-index: 5;
    pointer-events: none;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    border-radius: 999px;
    background: #1e1b4b;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.3px;
    box-shadow: 0 10px 24px rgba(30,27,75,0.35);
    animation: pi-hint-bob 1.8s ease-in-out infinite;
}

/* ---- DESIGN CODE + BRAND CODE: bigger and highlighted ---- */
.pi-root input.ant-input.pi-design-input {
    height: 42px !important;
    min-height: 42px !important;
    font-size: 20px !important;
    font-weight: 800 !important;
    letter-spacing: 1.2px;
    color: #4338ca !important;
    background: linear-gradient(135deg,#eef2ff,#e0e7ff) !important;
    border: 1.5px solid #a5b4fc !important;
    box-shadow: 0 2px 8px rgba(79,70,229,0.18);
}
.pi-root input.ant-input.pi-brand-input {
    height: 42px !important;
    min-height: 42px !important;
    font-size: 18px !important;
    font-weight: 800 !important;
    color: #92400e !important;
    background: linear-gradient(135deg,#fffbeb,#fef3c7) !important;
    border: 1.5px solid #fbbf24 !important;
    box-shadow: 0 2px 8px rgba(245,158,11,0.22);
}
.pi-root input.ant-input.pi-brand-input:focus {
    border-color: #f59e0b !important;
    box-shadow: 0 0 0 3px rgba(245,158,11,0.25);
}

/* ---- items table: bigger header + data text ---- */
.pi-table .ant-table-thead > tr > th {
    font-size: 12.5px !important;
    padding: 9px 10px !important;
    line-height: 1.25 !important;
    white-space: nowrap !important;
}
.pi-table .ant-table-tbody > tr > td {
    font-size: 13.5px !important;
    padding: 8px 10px !important;
    white-space: nowrap;
}
.pi-table .pi-row-edit > td { background: #eef2ff !important; }
/* body takes ALL the height left above the footer (--pi-ty is measured in JS) */
.pi-table .ant-table-body { height: var(--pi-ty, 300px) !important; max-height: var(--pi-ty, 300px) !important; }

/* "Added items" heading row above the table */
.pi-table-head {
    display: flex;
    align-items: center;
    gap: 9px;
    height: 48px;
    box-sizing: border-box;
    padding: 12px 14px 4px;
    flex-shrink: 0;
}

/* ════════ WHITE "TICKET" CARDS ════════
   white card + soft shadow, small hole at the top centre,
   dashed tear line with two side notches, stub below it.        */
.pi-card {
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 16px 12px 12px;
    flex-shrink: 0;
    background: #fff;
    border-radius: 18px;
    box-shadow: 0 12px 30px rgba(15,23,42,0.10), 0 2px 6px rgba(15,23,42,0.06);
}
/* supplier card has its own soft blue background */
.pi-card.pi-supplier-card { background: linear-gradient(180deg,#eff6ff 0%,#e0e7ff 100%); }
.pi-hole {
    position: absolute;
    top: 4px;
    left: 50%;
    width: 10px;
    height: 10px;
    margin-left: -5px;
    border-radius: 50%;
    background: var(--pi-bg);
    box-shadow: inset 0 1px 2px rgba(15,23,42,0.28);
}
.pi-card-head { display: flex; align-items: center; gap: 9px; margin-bottom: 6px; }
.pi-card-icon {
    width: 26px;
    height: 26px;
    flex-shrink: 0;
    border-radius: 10px;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    box-shadow: 0 4px 10px rgba(15,23,42,0.18);
}
.pi-card-title { font-size: 15px; font-weight: 800; color: #0f172a; letter-spacing: -0.2px; }
.pi-card-body { display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 0; }
.pi-pill {
    font-size: 11px;
    font-weight: 700;
    padding: 2px 10px;
    border-radius: 999px;
    white-space: nowrap;
}
.pi-perf {
    position: relative;
    height: 0;
    margin: 8px -12px 8px;
    border-top: 2px dashed #d5dde8;
    flex-shrink: 0;
}
.pi-perf i {
    position: absolute;
    top: -11px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--pi-bg);
}
.pi-perf i:first-child { left: -10px; box-shadow: inset -2px 0 3px rgba(15,23,42,0.14); }
.pi-perf i:last-child  { right: -10px; box-shadow: inset 2px 0 3px rgba(15,23,42,0.14); }
.pi-supplier-card .pi-perf { border-top-color: #a5b4fc; }
.pi-card-foot { flex-shrink: 0; }
@keyframes pi-glow {
    0%   { box-shadow: 0 0 0 0 rgba(249,115,22,0.55); }
    70%  { box-shadow: 0 0 0 8px rgba(249,115,22,0); }
    100% { box-shadow: 0 0 0 0 rgba(249,115,22,0); }
}
/* ---- supplier header buttons ---- */
.pi-browse {
    height: 26px;
    padding: 0 12px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11.5px;
    font-weight: 700;
    color: #fff;
    background: linear-gradient(135deg,#f97316 0%,#ec4899 100%);
    box-shadow: 0 3px 8px rgba(235, 133, 37, 0.35);
    animation: pi-glow 1.8s ease-out infinite;
    transition: transform 0.15s;
}
.pi-browse:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: none; }
/* keyboard focus (Enter flow) – clear ring so the user always sees where the cursor is */
.pi-browse:focus-visible { outline: 3px solid #1e1b4b; outline-offset: 2px; }

/* ---- "More info" button (top right of Purchase Values) ---- */
.pi-info-btn {
    height: 24px;
    padding: 0 10px;
    border-radius: 999px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 800;
    color: #b45309;
    background: #fffbeb;
    border: 1px solid #fcd34d;
    transition: background 0.15s, transform 0.15s;
}
.pi-info-btn:hover { background: #fef3c7; transform: translateY(-1px); }

/* ---- "More info" dialog ---- */
.pi-code-chip {
    height: 24px;
    padding: 0 10px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: linear-gradient(135deg,#eef2ff,#e0e7ff);
    border: 1px solid #a5b4fc;
    color: #3730a3;
}
.pi-code-chip small { font-size: 9.5px; font-weight: 800; letter-spacing: 0.8px; color: #6366f1; }
.pi-code-chip b { font-size: 14px; font-weight: 800; letter-spacing: 1.5px; font-family: monospace; }
.pi-info-hero {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-radius: 14px;
    color: #78350f;
    background: linear-gradient(135deg,#fde68a 0%,#fbbf24 100%);
    box-shadow: 0 6px 16px rgba(245,158,11,0.3);
}
.pi-info-hero span { font-size: 11px; font-weight: 800; letter-spacing: 1px; }
.pi-info-hero b { font-size: 28px; font-weight: 800; line-height: 1; }
.pi-info-sec {
    margin: 12px 0 4px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #92400e;
}
.pi-info-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 7px 4px;
    border-bottom: 1px dashed #e2e8f0;
}
.pi-info-row .pi-info-l { display: flex; flex-direction: column; line-height: 1.2; }
.pi-info-row .pi-info-l span { font-size: 13px; font-weight: 600; color: #334155; }
.pi-info-row .pi-info-l small { font-size: 11px; color: #94a3b8; }
.pi-info-row b { font-size: 14px; font-weight: 800; color: #0f172a; white-space: nowrap; }
.pi-info-row.sub { padding-left: 18px; }
.pi-info-row.sub .pi-info-l span { font-size: 12px; font-weight: 500; color: #64748b; }
.pi-info-row.sub b { font-size: 13px; font-weight: 700; color: #475569; }
.pi-info-row.strong { background: #fffbeb; border-radius: 8px; border-bottom: none; margin-top: 2px; }
.pi-info-row.strong .pi-info-l span { font-weight: 800; color: #92400e; }
.pi-info-empty {
    margin-top: 10px;
    padding: 8px 12px;
    border-radius: 10px;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    color: #64748b;
    font-size: 12px;
    font-weight: 600;
    text-align: center;
}

/* ---- footer amount rows (old-screen layout: label left, boxes right) ---- */
.pi-lrow { display: grid; grid-template-columns: 124px 1fr; align-items: center; gap: 6px; }
.pi-lrow-l { font-size: 13px; font-weight: 600; color: #475569; display: flex; align-items: center; gap: 5px; }
.pi-lrow-l.mark { color: #b91c1c; font-weight: 800; }
.pi-diamond { width: 8px; height: 8px; background: #dc2626; transform: rotate(45deg); display: inline-block; }
.pi-lrow-f { display: flex; align-items: center; gap: 6px; min-width: 0; }
.pi-lrow-f > .ant-input, .pi-lrow-f > input { flex: 1; min-width: 0; }
.pi-mini { font-size: 12.5px; font-weight: 600; color: #64748b; white-space: nowrap; }
.pi-net {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 6px 0;
    color: #9a4212;
}
.pi-net span { font-size: 1rem; font-weight: 800; letter-spacing: 0.5px; text-transform: uppercase; }
.pi-net b { font-size: 32px; font-weight: 800; line-height: 1; }

/* + Value / Discount / GST: three boxes, each with its own label above */
.pi-tri { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.pi-tri-c { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.pi-tri-l { font-size: 13px; font-weight: 700; color: #475569; display: flex; align-items: center; gap: 5px; }
.pi-tri-l i { width: 7px; height: 7px; border-radius: 2px; display: inline-block; }

/* ---- TOP STRIP (purchase type / entry no / entry date) ---- */
@keyframes pi-shine {
    0%   { transform: translateX(-120%) skewX(-18deg); }
    100% { transform: translateX(420%) skewX(-18deg); }
}
.pi-strip { position: relative; overflow: hidden; }
.pi-strip::before {
    content: "";
    position: absolute;
    top: 0; bottom: 0; left: 0;
    width: 25%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.16), transparent);
    animation: pi-shine 7s ease-in-out infinite;
    pointer-events: none;
}
.pi-strip > * { position: relative; z-index: 1; }

/* purchase type select: solid white box + dark bold text, also when locked */
.pi-strip .ant-select .ant-select-selector {
    background: #fff !important;
    border: 1px solid #fff !important;
    border-radius: 9px !important;
    box-shadow: 0 3px 10px rgba(0,0,0,0.25);
}
.pi-strip .ant-select .ant-select-selection-item {
    color: #1e1b4b !important;
    -webkit-text-fill-color: #1e1b4b !important;
    font-weight: 800 !important;
    font-size: 14px !important;
}
.pi-strip .ant-select .ant-select-selection-placeholder { color: #6366f1 !important; font-weight: 600; }
.pi-strip .ant-select-disabled .ant-select-selector { background: #fff !important; opacity: 1 !important; }
.pi-strip .ant-select .ant-select-arrow { color: #4338ca !important; }

.pi-reset {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.45);
    background: rgba(255,255,255,0.15);
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    transition: background 0.2s;
}
.pi-reset:hover { background: rgba(255,255,255,0.32); }
.pi-reset .anticon { transition: transform 0.45s; }
.pi-reset:hover .anticon { transform: rotate(180deg); }
/* saved-invoices button sits beside reset: same glass circle, no spin */
.pi-reset.pi-list .anticon { transition: transform 0.2s; }
.pi-reset.pi-list:hover .anticon { transform: scale(1.15); }

/* ---- TRANSPORT button ---- */
@keyframes pi-car-drive {
    0%   { transform: translateX(-3px); }
    50%  { transform: translateX(4px); }
    100% { transform: translateX(-3px); }
}

    @keyframes pi-trans {
    0%   { box-shadow: 0 0 0 0 rgba(22, 94, 249, 0.55); }
    70%  { box-shadow: 0 0 0 8px rgba(82, 22, 249, 0); }
    100% { box-shadow: 0 0 0 0 rgba(22, 26, 249, 0); }
}
.pi-transport-btn {
    height: var(--pi-h);
    width: 100%;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: #000;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.3px;
    background: linear-gradient(120deg, #b4bff0, #b9ceec, #b5bde4, #b6c8ea);
    animation: pi-trans 1.8s ease-out infinite;
    transition: transform 0.15s;
}
.pi-transport-btn:hover { transform: translateY(-1px); }
.pi-transport-btn .pi-car {
    font-size: 16px;
    display: inline-flex;
    animation: pi-car-drive 1.1s ease-in-out infinite;
}

/* ---- SALE ROUND-OFF button: compact pill that sits next to Add Item ---- */
@keyframes pi-bgmove { 0% { background-position: 0% 50%; } 100% { background-position: 300% 50%; } }
@keyframes pi-gear   { to { transform: rotate(360deg); } }
@keyframes pi-ring {
    0%   { box-shadow: 0 0 0 0 rgba(236,72,153,0.5); }
    70%  { box-shadow: 0 0 0 8px rgba(236,72,153,0); }
    100% { box-shadow: 0 0 0 0 rgba(236,72,153,0); }
}
.pi-round-btn {
    height: 46px;
    flex-shrink: 0;
    padding: 0 16px 0 6px;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    color: #000;
    display: flex;
    align-items: center;
    gap: 9px;
    text-align: left;
    background: linear-gradient(120deg, #b4bff0, #b9ceec, #b5bde4, #b6c8ea);
    background-size: 300% 100%;
    animation: pi-bgmove 7s linear infinite, pi-ring 2s ease-out infinite;
    transition: transform 0.15s;
}
.pi-round-btn:hover { transform: translateY(-1px); }
.pi-round-btn .pi-gear {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: rgba(255,255,255,0.25);
    border: 1px solid rgba(255,255,255,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
}
.pi-round-btn:hover .pi-gear .anticon { animation: pi-gear 1.1s linear infinite; }
.pi-round-title { font-size: 12.5px; font-weight: 800; letter-spacing: 0.5px; line-height: 1.1; }
.pi-round-sum { font-size: 11px; opacity: 0.92; line-height: 1.1; }

/* round-off dialog chips */
.pi-chip-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.pi-chip {
    height: 38px;
    border-radius: 10px;
    border: 1.5px solid #e2e8f0;
    background: #f8fafc;
    color: #475569;
    font-size: 12.5px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s;
}
.pi-chip:hover { border-color: #f9a8d4; color: #be185d; }
.pi-chip.on {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg,#ec4899,#f43f5e);
    box-shadow: 0 4px 10px rgba(236,72,153,0.4);
}

/* ---- item blocks: same white ticket card, just tighter ---- */
.pi-sub { padding: 15px 12px 11px; border-radius: 14px; }
.pi-sub .pi-hole { top: 3px; width: 8px; height: 8px; margin-left: -4px; }
.pi-sub .pi-card-head { margin-bottom: 5px; gap: 8px; }
.pi-sub .pi-card-icon { width: 24px; height: 24px; border-radius: 7px; font-size: 13px; box-shadow: none; }
.pi-sub .pi-card-title { font-size: 14.5px; }

/* ---- TOTAL QTY: a plain white stat card, half the old height ---- */
.pi-qty {
    flex: 0 0 auto;
    height: 96px;
    min-height: 0;
    border-radius: 16px;
    background: #fff;
    border: 1.5px dashed #fdba74;
    box-shadow: 0 12px 30px rgba(15,23,42,0.10), 0 2px 6px rgba(15,23,42,0.06);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
}
.pi-qty-l { font-size: 11px; font-weight: 800; letter-spacing: 0.8px; color: #9a3412; }
.pi-qty b { font-size: 30px; line-height: 1; font-weight: 800; color: #0f172a; }

/* ════════ ACTION BUTTONS: ONE style (the old Save pill) for Save / Save & Print / Cancel ════════
   only the 4 colour variables change per button */
.pi-act {
    position: relative;
    overflow: hidden;
    width: 100%;
    flex: 1 1 0;
    min-height: 45px;
    max-height: 60px;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 16px 0 6px;
    border-radius: 999px;
    color: #fff;
    font-weight: 800;
    font-size: 14px;
    letter-spacing: 0.8px;
    background: linear-gradient(110deg, var(--b1), var(--b2), var(--b3), var(--b2), var(--b1));
    background-size: 300% 100%;
    animation: pi-bgmove 8s linear infinite;
    animation-delay: var(--bd, 0s);
    box-shadow: 0 6px 14px var(--bs);
    transition: transform 0.15s, box-shadow 0.2s;
}
.pi-act:disabled { opacity: 0.5; cursor: not-allowed; }
.pi-act:active:not(:disabled) { transform: scale(0.97); }
.pi-act::before {
    content: "";
    position: absolute;
    top: 0; bottom: 0; left: 0;
    width: 30%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
    transform: translateX(-130%) skewX(-18deg);
    pointer-events: none;
}
.pi-act::before { animation: pi-shine 3.6s ease-in-out infinite; animation-delay: var(--bd, 0s); }
.pi-act:hover:not(:disabled)::before { animation-duration: 1.2s; }
.pi-act .pi-coin {
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background: #fff;
    color: var(--bc);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 21px;
    flex-shrink: 0;
    transition: transform 0.4s;
}
.pi-act:hover:not(:disabled) .pi-coin { transform: rotate(360deg); }
.pi-act span.pi-txt { flex: 1; text-align: center; }

.pi-b-save   { --b1:#10b981; --b2:#047857; --b3:#34d399; --bs:rgba(5,150,105,0.38);  --bc:#059669; --bd:0s; }
.pi-b-print  { --b1:#f59e0b; --b2:#d97706; --b3:#fbbf24; --bs:rgba(217,119,6,0.38);  --bc:#d97706; --bd:1.2s; }
.pi-b-cancel { --b1:#f43f5e; --b2:#be123c; --b3:#fb7185; --bs:rgba(190,18,60,0.35);  --bc:#e11d48; --bd:2.4s; }
@media (prefers-reduced-motion: reduce) { .pi-act, .pi-act::before { animation: none !important; } }

/* ---- DIALOGS: own themed header band ---- */
.pi-modal .ant-modal-content {
    padding: 0 !important;
    overflow: hidden;
    border-radius: 14px !important;
}
.pi-modal .ant-modal-close {
    top: 12px !important;
    inset-inline-end: 12px !important;
    color: #fff !important;
    z-index: 3;
}
.pi-modal .ant-modal-close:hover {
    background: rgba(255,255,255,0.25) !important;
    color: #fff !important;
}
.pi-modal .ant-modal-footer { margin: 0 !important; padding: 8px 16px 14px !important; }
.pi-dlg-head {
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 52px 14px 16px;
    color: #fff;
}
.pi-dlg-head::after {
    content: "";
    position: absolute;
    right: -30px;
    top: -45px;
    width: 130px;
    height: 130px;
    border-radius: 50%;
    background: rgba(255,255,255,0.13);
}
.pi-dlg-icon {
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    border-radius: 11px;
    background: rgba(255,255,255,0.2);
    border: 1px solid rgba(255,255,255,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 19px;
    position: relative;
    z-index: 1;
}
.pi-dlg-text { position: relative; z-index: 1; line-height: 1.2; }
.pi-dlg-title { font-size: 15px; font-weight: 800; letter-spacing: 0.3px; }
.pi-dlg-sub { font-size: 11px; opacity: 0.85; margin-top: 1px; }
.pi-dlg-body { padding: 12px 16px 6px; }

/* ---- SUPPLIER BROWSE TABLE (blue theme) – also used by the saved-invoices list ---- */
.pi-sup-wrap {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 10px rgba(15,23,42,0.06);
}
.pi-sup-table .ant-table-thead > tr > th {
    background: linear-gradient(90deg,#eff6ff 0%,#e0e7ff 100%) !important;
    color: #1d4ed8 !important;
    font-size: 11px !important;
    font-weight: 800 !important;
    letter-spacing: 0.7px;
    text-transform: uppercase;
    padding: 9px 12px !important;
    border-bottom: 1px solid #c7d2fe !important;
}
.pi-sup-table .ant-table-thead > tr > th::before { display: none !important; }
.pi-sup-table .ant-table-tbody > tr > td {
    padding: 8px 12px !important;
    border-bottom: 1px solid #eef2f7 !important;
    transition: background 0.15s;
}
.pi-sup-table .ant-table-tbody > tr:nth-child(even) > td { background: #fafcff; }
.pi-sup-table .ant-table-tbody > tr:hover > td { background: #eff6ff !important; }
.pi-sup-table .pi-sup-go { color: #94a3b8; transition: transform 0.15s, color 0.15s; }
.pi-sup-table .ant-table-tbody > tr:hover .pi-sup-go {
    color: #2563eb;
    transform: translateX(3px);
}

/* ---- saved-invoices filter panel ---- */
.pi-list-filters {
    display: grid;
    grid-template-columns: 1.3fr 1fr 1.2fr 1fr auto;
    gap: 10px;
    align-items: end;
    padding: 8px 10px 10px;
    margin-bottom: 10px;
    border-radius: 10px;
    background: linear-gradient(135deg,#eff6ff 0%,#e0e7ff 100%);
    border: 1px solid #c7d2fe;
}
.pi-clear-link { margin-left: 10px; font-weight: 700; color: #dc2626; cursor: pointer; }
.pi-clear-link:hover { text-decoration: underline; }

`;

/* ════════════════════════════════════════════════════════════════
   SMALL UI HELPERS
   ════════════════════════════════════════════════════════════════ */

// label + control. `span` = grid columns it takes inside a grid parent
const Fld = ({ label, span = 1, color, children }) => (
    <div
        style={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minWidth: 0,
            gridColumn: `span ${span}`,
        }}
    >
        <span style={{ ...S.label, color: color || S.label.color }}>{label}</span>
        {children}
    </div>
);

// pick-only dropdown (purchase type, city, transport)
// inputRef = ref used by the Enter-key flow, onSelect = fires even when the same value is picked again
// onFocus  = lets the screen load that master table the first time the field is used
const Sel = ({ value, onChange, onSelect, options, placeholder, disabled, loading, inputRef, onFocus }) => (
    <Select
        ref={inputRef}
        showSearch
        allowClear
        value={value}
        onChange={(v) => onChange(v ?? null)}
        onSelect={onSelect}
        onFocus={onFocus}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        loading={loading}
        optionFilterProp="label"
        style={{ width: "100%" }}
    />
);

// pick from the list OR type a new value
// defaultActiveFirstOption={false}: Enter keeps what the user typed (a new value) instead of
// silently replacing it with the first match. Pick a list item with the arrow keys or the mouse.
const Auto = ({ value, onChange, onSelect, options, placeholder, inputRef, onKeyDown, onOpen, onFocus }) => (
    <AutoComplete
        ref={inputRef}
        allowClear
        defaultActiveFirstOption={false}
        value={value}
        onChange={(v) => onChange(v ?? "")}
        onSelect={onSelect}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onDropdownVisibleChange={onOpen}
        options={options}
        placeholder={placeholder}
        filterOption={(input, opt) =>
            String(opt?.value ?? "")
                .toUpperCase()
                .includes(input.toUpperCase())
        }
        style={{ width: "100%" }}
    />
);

// item block = the same white ticket card as Supplier / Invoice, only tighter.
// `template` = custom grid-template-columns (for narrow value fields)
// `accent`   = heading icon colour (see ACCENT above)
// `extra`    = anything shown at the top right of the heading (e.g. More info)
const SubCard = ({ title, icon, accent = ACCENT.item, cols = 4, template, extra, children }) => (
    <Card compact title={title} icon={icon} accent={accent} extra={extra}>
        <div
            className="pi-tight"
            style={{
                display: "grid",
                gridTemplateColumns: template || `repeat(${cols},1fr)`,
                gap: "2px 6px",
            }}
        >
            {children}
        </div>
    </Card>
);

/* White ticket card: round icon tile + bold title (+ pill / buttons) on top,
   body in the middle, optional dashed tear line with a stub (footer) below it. */
const Card = ({
    title,
    icon,
    accent,
    pill,
    extra,
    footer,
    hole = true,
    compact,
    className = "",
    children,
    style,
    bodyStyle,
}) => (
    <div
        className={`pi-card${compact ? " pi-sub" : ""}${className ? ` ${className}` : ""}`}
        style={style}
    >
        {hole && <span className="pi-hole" />}
        <div className="pi-card-head">
            <span className="pi-card-icon" style={{ background: accent }}>
                {icon}
            </span>
            <span className="pi-card-title">{title}</span>
            <span
                style={{
                    marginLeft: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                }}
            >
                {pill}
                {extra}
            </span>
        </div>
        <div className="pi-card-body" style={bodyStyle}>
            {children}
        </div>
        {footer && (
            <>
                <div className="pi-perf">
                    <i />
                    <i />
                </div>
                <div className="pi-card-foot">{footer}</div>
            </>
        )}
    </div>
);

// small soft pill shown on the right of a card title
const Pill = ({ children, bg, color }) => (
    <span className="pi-pill" style={{ background: bg, color }}>
        {children}
    </span>
);

// footer amount row (old-screen layout): label on the left, boxes on the right
const LRow = ({ label, mark, children }) => (
    <div className="pi-lrow">
        <span className={`pi-lrow-l${mark ? " mark" : ""}`}>
            {mark && <i className="pi-diamond" />}
            {label}
        </span>
        <div className="pi-lrow-f">{children}</div>
    </div>
);
const ROUND_HINT = {
    GENERAL: "Cust Price is kept exactly as calculated",
    "0": "Cust Price ends in 0 · 612 → 610, 667 → 670",
    "5": "Cust Price ends in 5 · 612 → 615, 618 → 615",
    "9": "Cust Price ends in 9 · 814 → 819, 886 → 889",
};
// one line inside the "More info" dialog: label (+ small hint) on the left, value on the right
const InfoRow = ({ label, hint, value, sub, strong, tone }) => (
    <div className={`pi-info-row${sub ? " sub" : ""}${strong ? " strong" : ""}`}>
        <div className="pi-info-l">
            <span>{label}</span>
            {hint && <small>{hint}</small>}
        </div>
        <b style={tone ? { color: tone } : undefined}>{value}</b>
    </div>
);

// themed header band used inside the dialogs
const DialogHead = ({ icon, title, subtitle, gradient }) => (
    <div className="pi-dlg-head" style={{ background: gradient }}>
        <div className="pi-dlg-icon">{icon}</div>
        <div className="pi-dlg-text">
            <div className="pi-dlg-title">{title}</div>
            <div className="pi-dlg-sub">{subtitle}</div>
        </div>
    </div>
);

// small square icon button (supplier card header – coloured, visible on the card)
const iconBtn = (color) => ({
    width: 26,
    height: 26,
    borderRadius: 8,
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    color,
    background: `${color}14`,
    border: `1px solid ${color}40`,
    cursor: "pointer",
});

// small coloured info tile (SHORT / PAN / GSTIN) in the supplier card stub
const Tile = ({ label, value, bg, border, labelColor, flex = 1 }) => (
    <div
        title={value}
        style={{
            flex,
            minWidth: 0,
            background: bg,
            border: `1px solid ${border}`,
            borderRadius: 8,
            padding: "2px 7px",
        }}
    >
        <div
            style={{
                fontSize: "0.62rem",
                fontWeight: 800,
                letterSpacing: 0.4,
                color: labelColor,
            }}
        >
            {label}
        </div>
        <div
            style={{
                fontSize: "0.75rem",
                fontFamily: "monospace",
                fontWeight: 700,
                color: "#334155",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }}
        >
            {value || "—"}
        </div>
    </div>
);

// keepType = true  → the purchase type stays (quick repeat entry)
// keepType = false → the purchase type is cleared too (after the final Save / Update)
const emptyHeader = (prev = {}) => ({
    purchaseType: prev.purchaseType || null,
    city: null,
    supplier: null,
    invoiceNo: "",
    invoiceDate: dayjs(),
    stockRecDate: dayjs(),
    transport: null,
    lrNo: "",
    lrDate: null,
    dueDays: "0",
    marginPer: "0",
    isA: false,
    isB: false,
});

// NOTE: Token field was removed from the screen. The payload builder reads
// num(r.token) so it is saved as 0 without any change in the data file.
// gstMode: "auto"   → GST % comes from the PURCHASE_TYPE slab (re-worked when Gross DP changes)
//          "manual" → the user typed GST (or it was loaded from a saved invoice) and it stays
//                     until Gross DP is edited again
const emptyItem = () => ({
    brandCode: "", // typed by user, saved as 0 when empty
    brandName: "",
    itemCategory: "",
    itemName: "",
    hsn: "",
    size: "",
    colour: "",
    fitting: "",
    offer: "",
    qty: "",
    free: "",
    grossDp: "",
    plusPer: "",
    purDisPer: "",
    gstPer: "",
    gstMode: "auto",
    wPer: "",
    salePer: "",
    custPrice: "",
    saleMode: "per", // which of Sale % / Cust Price the user typed last
    disPer: "", // optional – saved as 0 when empty
});

const emptyFooter = () => ({
    roundoff: "GENERAL",
    setFloor: false,
    otherAdditions: "",
    tradeDisPer: "",
    adjustment: "",
    freight: "",
    otherChrgs: "",
    narration: "",
});

/* ════════════════════════════════════════════════════════════════
   COMPONENT
   ════════════════════════════════════════════════════════════════ */

const PurchaseInvoice = () => {
    const tenantName = localStorage.getItem("tenantName");
    const [messageApi, contextHolder] = message.useMessage();

    /* ── ENTER-KEY FLOW: refs ──────────────────────────────────────
       R         : key → the field's ref (Input / Select / AutoComplete / DatePicker / button)
       openMap   : key → is that AutoComplete's dropdown open right now
       latest    : always holds the newest state, so key handlers and delayed
                   focus calls never read an old value                        */
    const R = useRef({});
    const openMap = useRef({});
    const latest = useRef({});

    // ref callback: <Input ref={reg("invoiceNo")} />
    const reg = (key) => (el) => {
        if (el) R.current[key] = el;
    };

    // move the cursor to a field. Small delay so a field that was disabled a moment ago
    // (purchase type just picked, dialog just closed) is ready to receive focus.
    const focusField = (key, delay = 60) =>
        setTimeout(() => {
            const el = R.current[key];
            if (!el || typeof el.focus !== "function") return;
            try {
                el.focus({ cursor: "all" }); // Inputs: select the text, so typing replaces it
            } catch {
                el.focus();
            }
        }, delay);

    const [saving, setSaving] = useState(false);

    // MASTERS ARE LOADED LAZILY: each table is fetched only when its field / dialog is first
    // used (ensureMaster below) and cached after that. No full-screen loader any more.
    const [masters, setMasters] = useState({});
    const [masterLoading, setMasterLoading] = useState({}); // key → spinner on that dropdown
    const masterReq = useRef({}); // key → promise (so each table is fetched only once)

    // prefix / entryPrefix come from counter_config (page open).
    // maxSeq comes from purchInvoice_DataDet (rows where PType = the selected purchase type,
    // highest MAXNUM) and stays null until the entry no is created, or a saved invoice is opened.
    const [designBase, setDesignBase] = useState({ prefix: null, entryPrefix: "", maxSeq: null });
    const [suppliers, setSuppliers] = useState([]);
    const [supplierLoading, setSupplierLoading] = useState(false);

    // ENTRY NO = one per invoice. entryTick forces a fresh number after Reset.
    const [entryNo, setEntryNo] = useState("");
    const [entryTick, setEntryTick] = useState(0);

    const [header, setHeader] = useState(emptyHeader());
    const [item, setItem] = useState(emptyItem());
    const [rows, setRows] = useState([]);
    const [footer, setFooter] = useState(emptyFooter());

    // true after the first item was saved (Save API done)
    const [invoiceSaved, setInvoiceSaved] = useState(false);
    // true when the invoice already exists in TRANS_ENTRY_DATA (it was loaded from the
    // saved list) → every item call is Update, and the final button calls
    // CurrentStockApi/Update instead of /Save
    const [stockSaved, setStockSaved] = useState(false);
    // original entry date of a loaded invoice (kept on every Update)
    const [savedEntryDate, setSavedEntryDate] = useState(null);
    // totals of a saved invoice exactly as stored in TRANS_ENTRY_DATA (qty + net amount).
    // Shown right after opening it from the list; cleared as soon as items / charges change.
    const [savedTotals, setSavedTotals] = useState(null);
    // set the moment a saved invoice is opened: stops the Entry No effect from
    // replacing the loaded entry no with a brand-new one
    const editingRef = useRef(false);
    // set when a supplier is picked in the Browse dialog: the cursor goes to Invoice No
    // only AFTER the dialog has fully closed (the dialog hands focus back to the Browse
    // button on close, which would otherwise steal the cursor)
    const focusInvoiceAfterBrowse = useRef(false);
    // how many design-code numbers this invoice has used (never reused)
    const [seqUsed, setSeqUsed] = useState(0);

    // index of the table row currently loaded back into the entry form (null = adding new)
    const [editIndex, setEditIndex] = useState(null);

    // supplier browse dialog: its own city filter + name filter
    const [browseOpen, setBrowseOpen] = useState(false);
    const [browseCity, setBrowseCity] = useState(null);
    const [browseText, setBrowseText] = useState("");
    // the supplier row picked in the dialog (shown in the supplier card)
    const [selectedSupplier, setSelectedSupplier] = useState(null);

    // saved purchase invoices dialog (list from TRANS_ENTRY_DATA)
    const [listOpen, setListOpen] = useState(false);
    const [listLoading, setListLoading] = useState(false);
    const [listRows, setListRows] = useState([]);
    // filters of the saved-invoices dialog: search text + city / supplier / type dropdowns
    const [listText, setListText] = useState("");
    const [listCity, setListCity] = useState(null);
    const [listSupplier, setListSupplier] = useState(null);
    const [listType, setListType] = useState(null);
    // entry no of the invoice being opened (spinner while its data is read)
    const [openingNo, setOpeningNo] = useState(null);

    // transport mini dialog (Transport + L R No + L R Date)
    const [transportOpen, setTransportOpen] = useState(false);
    // sale round-off dialog (General/5/9/0 rupees + set floor + narration)
    const [roundOpen, setRoundOpen] = useState(false);
    // purchase values "More info" dialog (full amount breakdown)
    const [infoOpen, setInfoOpen] = useState(false);

    // table body gets a FIXED height = whatever room is left above the footer
    const tableWrapRef = useRef(null);
    const [tableY, setTableY] = useState(300);
    useEffect(() => {
        const el = tableWrapRef.current;
        if (!el || typeof ResizeObserver === "undefined") return undefined;
        const ro = new ResizeObserver(() => {
            // 48px = "Added items" heading row, 40px ≈ the table header row;
            // the rest is the scrolling body
            setTableY(Math.max(120, el.clientHeight - 48 - 40));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    /* ── counter_config only (barcode prefix + entry prefix) – small table, page open ── */
    const loadBase = useCallback(
        () =>
            fetchCounterConfig(tenantName)
                .then((cfg) => setDesignBase((p) => ({ ...p, ...cfg })))
                .catch((e) => {
                    console.error(e);
                    messageApi.error("Could not load design code settings.");
                }),
        [tenantName, messageApi],
    );

    /* ── purchInvoice_DataDet → highest MAXNUM of the GIVEN PURCHASE TYPE
          (where PType = '<purchase type name>').
          Highest MAXNUM + 1 = the next design code number.
          Called right after the entry no is created (purchase type picked / Reset)
          and when a saved invoice is opened (with that invoice's purchase type). ── */
    const loadMaxSeq = useCallback(
        (purchaseTypeName) =>
            fetchMaxSeq(tenantName, purchaseTypeName)
                .then((maxSeq) => setDesignBase((p) => ({ ...p, maxSeq })))
                .catch((e) => {
                    console.error(e);
                    messageApi.error("Could not read the last design sequence.");
                }),
        [tenantName, messageApi],
    );

    /* ── LAZY MASTER LOADER ──
       ensureMaster("company") fetches COMPANY_MAST the first time it is called and
       returns the rows (a cached promise afterwards, so it is safe to call on every focus).
       A failed load is forgotten so the next focus tries again. */
    const ensureMaster = useCallback(
        (key) => {
            if (masterReq.current[key]) return masterReq.current[key];
            setMasterLoading((p) => ({ ...p, [key]: true }));
            const p = loadMaster(key, tenantName)
                .then((list) => {
                    setMasters((m) => ({ ...m, [key]: list }));
                    return list;
                })
                .catch((e) => {
                    console.error(`Failed to load ${key}`, e);
                    delete masterReq.current[key]; // allow a retry on the next focus
                    messageApi.error(`Could not load ${key}`);
                    return [];
                })
                .finally(() => setMasterLoading((m) => ({ ...m, [key]: false })));
            masterReq.current[key] = p;
            return p;
        },
        [tenantName, messageApi],
    );

    /* ── page open: ONLY purchase type (first field) + counter_config.
          Every other master waits until its field is used. ── */
    useEffect(() => {
        ensureMaster("purchaseType");
        loadBase();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // page open → the cursor starts in the Purchase Type box
    useEffect(() => {
        const t = focusField("purchaseType", 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── suppliers for the city chosen inside the browse dialog ── */
    useEffect(() => {
        if (!browseCity) {
            setSuppliers([]);
            return;
        }
        let cancelled = false;
        setSupplierLoading(true);
        fetchSuppliersByArea(tenantName, browseCity)
            .then((r) => !cancelled && setSuppliers(r))
            .catch((e) => {
                console.error("Supplier load failed", e);
                if (!cancelled) setSuppliers([]);
            })
            .finally(() => !cancelled && setSupplierLoading(false));
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [browseCity]);

    // HSN still fills when the category was typed / picked before the group master finished loading
    useEffect(() => {
        if (!masters.group || !item.itemCategory || item.hsn) return;
        const row = masters.group.find(
            (r) =>
                String(pick(r, FIELDS.group.name)).trim().toUpperCase() ===
                item.itemCategory.trim().toUpperCase(),
        );
        const h = row ? clean(pick(row, FIELDS.group.hsn)) : "";
        if (h) setItem((p) => (p.hsn ? p : { ...p, hsn: h }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [masters.group]);

    /* ── selected purchase type → PCode (design code) + PNo (entry no) ── */
    const purchaseTypeRow = (masters.purchaseType || []).find(
        (r) => pick(r, FIELDS.purchaseType.name) === header.purchaseType,
    );
    // ► if GST ever looks wrong, send me this console log (the row the GST rule reads)
    useEffect(() => {
        if (purchaseTypeRow) console.log("purchase type row:", purchaseTypeRow);
    }, [purchaseTypeRow]);
    const pcode = String(pick(purchaseTypeRow, FIELDS.purchaseType.code, ""));
    const pno = String(pick(purchaseTypeRow, FIELDS.purchaseType.no, ""));
    const fyCode = getFyearCode();

    // everything except the top strip stays disabled until a purchase type is picked
    const typeReady = !!header.purchaseType;

    /* ══════════ ENTRY NO (invoice level) ══════════
       When a purchase type is selected:
         GetDataFromGivenTableNameWithWhere → PURCHINVOICE_MAST where PType = selected type
         entry no = PNo + FY + Terminal id + (rows found + 1)
       Runs again when the purchase type changes, and after Reset (entryTick).
       It is NOT refetched while a saved invoice is open (its own entry no must stay).
       After the final Save / Update the purchase type is cleared, so the user picks it
       again and the next number is read fresh.
       It only needs counter_config (prefix != null) – NOT current_stock.

       DESIGN CODE MAX: as soon as the entry no is created, the highest MAXNUM of
       purchInvoice_DataDet WHERE PType = the selected purchase type is read (loadMaxSeq).
       Highest MAXNUM + 1 = next design code. The design code on the screen appears once
       it is ready. */
    useEffect(() => {
        if (editingRef.current) return undefined;

        // wait only for the purchase type, its PNo, and counter_config to finish loading.
        // The barcode prefix is NOT needed for entry no.
        if (!header.purchaseType || !pno || designBase.prefix === null) {
            setEntryNo("");
            return undefined;
        }
        if (!designBase.entryPrefix) {
            console.warn("Entry prefix not found in counter_config:", designBase);
            messageApi.warning("Entry prefix not found in counter_config – entry no may be wrong");
        }

        let cancelled = false;
        // a new entry no is being made → the old design sequence is no longer valid
        setDesignBase((p) => ({ ...p, maxSeq: null }));
        fetchNextEntryNo(tenantName, {
            purchaseType: header.purchaseType,
            purchaseTypeNo: pno,
            entryPrefix: designBase.entryPrefix,
            fyearCode: fyCode,
        })
            .then(async (no) => {
                if (cancelled) return;
                setEntryNo(no);
                await loadMaxSeq(header.purchaseType); // design code max: PType = this purchase type
            })
            .catch((e) => {
                console.error("Entry no failed", e);
                if (!cancelled) setEntryNo("");
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [header.purchaseType, pno, designBase.entryPrefix, designBase.prefix, entryTick]);

    /* ── dropdown options ── */
    const opts = useMemo(
        () => ({
            purchaseType: toOptions(masters.purchaseType, FIELDS.purchaseType.name),
            city: toOptions(masters.area, FIELDS.area.name),
            transport: toOptions(masters.transport, FIELDS.transport.name),
            brand: toOptions(masters.company, FIELDS.company.name),
            category: toOptions(masters.group, FIELDS.group.name),
            size: toOptions(masters.size, FIELDS.size.name),
            colour: toOptions(masters.colour, FIELDS.colour.name),
            fitting: toOptions(masters.fitting, FIELDS.fitting.name),
            offer: toOptions(masters.sleeves, FIELDS.sleeves.name),
        }),
        [masters],
    );

    // item names narrowed by the typed/selected category
    const itemOptions = useMemo(() => {
        const cat = item.itemCategory.trim().toUpperCase();
        return toOptions(
            (masters.item || []).filter(
                (r) =>
                    !cat ||
                    String(pick(r, FIELDS.item.group, "")).trim().toUpperCase() === cat,
            ),
            FIELDS.item.name,
        );
    }, [masters.item, item.itemCategory]);

    /* ── selected supplier details (kept in its own state so changing the
          city filter in the dialog does not wipe the card) ── */
    const supplier = selectedSupplier;
    const supplierName = supplier ? clean(pick(supplier, FIELDS.supplier.name)) : "";
    const supplierShort = clean(pick(supplier, FIELDS.supplier.shortName));
    const supplierAddress = supplier
        ? [
            ...FIELDS.supplier.address.map((k) => clean(pick(supplier, [k]))),
            clean(pick(supplier, FIELDS.supplier.area)),
        ]
            .filter(Boolean)
            .join(", ")
        : "";
    const station =
        clean(pick(supplier, FIELDS.supplier.station)).toUpperCase() || "LOCAL";

    // rows shown inside the supplier browse dialog (filtered by search text)
    const browseRows = useMemo(() => {
        const t = browseText.trim().toUpperCase();
        return suppliers.filter(
            (r) =>
                !t ||
                [
                    pick(r, FIELDS.supplier.name),
                    pick(r, FIELDS.supplier.shortName),
                    pick(r, FIELDS.supplier.gstin),
                ].some((v) => String(v ?? "").toUpperCase().includes(t)),
        );
    }, [suppliers, browseText]);

    // dropdown options for the saved-invoices filters (built from the loaded list,
    // so no extra API call). Supplier options are narrowed by the selected city.
    const listOpts = useMemo(() => {
        const uniq = (arr) =>
            [...new Set(arr.filter(Boolean))]
                .sort((a, b) => String(a).localeCompare(String(b)))
                .map((v) => ({ label: v, value: v }));
        return {
            city: uniq(listRows.map((r) => r.area)),
            supplier: uniq(
                listRows
                    .filter((r) => !listCity || r.area === listCity)
                    .map((r) => r.party),
            ),
            type: uniq(listRows.map((r) => r.type)),
        };
    }, [listRows, listCity]);

    // rows shown inside the saved-invoices dialog (dropdown filters + search text)
    const filteredList = useMemo(() => {
        const t = listText.trim().toUpperCase();
        return listRows.filter(
            (r) =>
                (!listCity || r.area === listCity) &&
                (!listSupplier || r.party === listSupplier) &&
                (!listType || r.type === listType) &&
                (!t ||
                    [r.entryNo, r.billNo, r.party, r.area, r.type].some((v) =>
                        String(v ?? "").toUpperCase().includes(t),
                    )),
        );
    }, [listRows, listText, listCity, listSupplier, listType]);

    const listFilterActive = !!(listText || listCity || listSupplier || listType);
    const clearListFilters = () => {
        setListText("");
        setListCity(null);
        setListSupplier(null);
        setListType(null);
    };

    /* ── handlers ── */
    const setH = (k, v) => setHeader((p) => ({ ...p, [k]: v }));
    const setI = (k, v) => setItem((p) => ({ ...p, [k]: v }));
    const setINum = (k, v) => /^\d*\.?\d{0,3}$/.test(v) && setI(k, v);
    const setF = (k, v) => setFooter((p) => ({ ...p, [k]: v }));
    // a charge / discount changed → the saved totals are no longer valid, use the live ones
    const setFNum = (k, v) => {
        if (!/^\d*\.?\d{0,2}$/.test(v)) return;
        setSavedTotals(null);
        setF(k, v);
    };

    // Sale % and Cust Price drive each other: the one typed last is the source
    const onSalePer = (v) =>
        /^\d*\.?\d{0,2}$/.test(v) &&
        setItem((p) => ({ ...p, salePer: v, saleMode: "per" }));
    const onCustPrice = (v) =>
        /^\d*\.?\d{0,2}$/.test(v) &&
        setItem((p) => ({ ...p, custPrice: v, saleMode: "price" }));

    // typing or picking a category fills HSN when the group exists
    const onCategoryChange = (v) => {
        const row = (masters.group || []).find(
            (r) =>
                String(pick(r, FIELDS.group.name)).trim().toUpperCase() ===
                v.trim().toUpperCase(),
        );
        const hsn = row ? clean(pick(row, FIELDS.group.hsn)) : "";
        setItem((p) => ({ ...p, itemCategory: v, hsn: hsn || p.hsn }));
    };
    // picking from the list means a different group → reset the item name
    const onCategorySelect = () => setI("itemName", "");

    /* ── supplier block ──
       ASSUMPTION: Edit / Delete need supplier-master APIs which are not part of
       this screen yet. Browse works now. Replace the two stubs when APIs are ready. */
    const openBrowse = () => {
        ensureMaster("area"); // city list is needed only when this dialog opens
        setBrowseText("");
        setBrowseCity(header.city); // start with the last used city
        setBrowseOpen(true);
    };
    // click a row in the dialog (or Enter in its search box) = use that supplier and close.
    // The cursor goes to Invoice No AFTER the dialog has fully closed (see afterOpenChange on the
    // Browse modal), because the dialog gives focus back to the Browse button when it is gone.
    const pickSupplier = (r) => {
        setSelectedSupplier(r);
        setHeader((p) => ({
            ...p,
            city: browseCity,
            supplier: pick(r, FIELDS.supplier.name),
        }));
        focusInvoiceAfterBrowse.current = true;
        setBrowseOpen(false);
        // safety net: if afterOpenChange does not fire, still move the cursor
        setTimeout(() => {
            if (focusInvoiceAfterBrowse.current) {
                focusInvoiceAfterBrowse.current = false;
                focusField("invoiceNo", 0);
            }
        }, 600);
    };
    const handleSupplierEdit = () => {
        if (!supplier) return messageApi.warning("Select a supplier first");
        messageApi.info("Supplier edit API will be added next");
    };
    const handleSupplierDelete = () => {
        messageApi.info("Supplier delete API will be added next");
    };

    // transport dialog helpers
    const transportFilled = !!(header.transport || header.lrNo || header.lrDate);
    const transportSummary = transportFilled
        ? [
            header.transport,
            header.lrNo && `LR ${header.lrNo}`,
            header.lrDate && dayjs(header.lrDate).format("DD-MMM-YYYY"),
        ]
            .filter(Boolean)
            .join(" · ")
        : "Click to add transport details";
    const clearTransport = () =>
        setHeader((p) => ({ ...p, transport: null, lrNo: "", lrDate: null }));
    const openTransport = () => {
        ensureMaster("transport"); // transport list is needed only when this dialog opens
        setTransportOpen(true);
    };

    // round-off dialog helpers
    const roundLabel =
        ROUND_OPTS.find((o) => o.value === footer.roundoff)?.label || "General";
    const clearRound = () =>
        setFooter((p) => ({ ...p, roundoff: "GENERAL", setFloor: false, narration: "" }));

    /* ══════════ DESIGN CODE (item level) ══════════
       Frozen on each row when it is added. The form shows the code the next
       new item will get, or the row's own code while editing.
       Next design code = highest MAXNUM of this purchase type + 1 (+ items already
       added on this invoice).
       Empty until the max sequence of this purchase type has been read. */
    const nextDesignCode =
        editIndex !== null
            ? rows[editIndex]?.designCode || ""
            : buildDesignCode(pcode, designBase, seqUsed);

    /* ══════════ AUTOMATIC GST (from the PURCHASE_TYPE row) ══════════
       autoGst  = % worked out from the purchase type + Gross DP (null = not applicable)
       showGst  = what the GST box shows AND what the calculation uses:
                    manual mode → the typed / saved value
                    auto mode   → autoGst (falls back to whatever is in the box)
       calcItem = the item with that GST, so every calculated box and the saved row agree. */
    const autoGst = resolveGstPer(purchaseTypeRow, item.grossDp);
    const showGst =
        item.gstMode === "manual"
            ? item.gstPer
            : autoGst !== null
                ? String(autoGst)
                : item.gstPer;
    const calcItem = { ...item, gstPer: showGst };

    const calc = calcPurchaseRow(calcItem, footer.roundoff);
    const codeMap = useMemo(
        () => buildCodeMap(masters.codeConfigure),
        [masters.codeConfigure],
    );
    const priceCode = buildPriceCode(calc.dirPrice, codeMap);
    const totals = calcTotals(rows, footer);

    // Gross DP changed → GST is worked out again from the slab (auto mode)
    const onGrossDp = (v) =>
        /^\d*\.?\d{0,3}$/.test(v) &&
        setItem((p) => ({ ...p, grossDp: v, gstMode: "auto" }));
    // GST typed by hand → it overrides the slab until Gross DP changes (empty = back to auto)
    const onGstPer = (v) =>
        /^\d*\.?\d{0,2}$/.test(v) &&
        setItem((p) => ({ ...p, gstPer: v, gstMode: v === "" ? "auto" : "manual" }));

    // what the Total Qty / Net Amount boxes show: the saved TRANS_ENTRY_DATA totals right
    // after an invoice is opened from the list, otherwise the live totals of the rows
    const shownQty = savedTotals ? savedTotals.totalQty : totals.totalQty;
    const shownNet = savedTotals ? savedTotals.netAmount : totals.netAmount;

    // numbers shown in the "More info" dialog (all derived from `calc`, nothing new is calculated for saving)
    const isOutStation = station === "OUTSTATION";
    const gstPerNum = num(showGst);
    const taxableAmt = calc.amount + calc.plusValue - calc.discValue;
    const infoHasData = num(item.qty) > 0 && num(item.grossDp) > 0;

    // what the Sale % / Cust Price boxes show (the typed one as typed, the other one calculated)
    const showSalePer =
        item.saleMode === "price"
            ? item.custPrice !== "" && calc.dirPrice > 0
                ? calc.salePer.toFixed(2)
                : ""
            : item.salePer;
    const showCustPrice =
        item.saleMode === "per"
            ? item.salePer !== "" && calc.dirPrice > 0
                ? calc.custPrice.toFixed(0)
                : ""
            : item.custPrice;

    // header locked once the invoice exists on the server (Delete key = invNo + supplier + city)
    const locked = invoiceSaved;

    /* ══════════ ENTER-KEY FLOW: logic ══════════
       latest.current is refreshed on EVERY render, so go() / advance() always see
       the newest values even when they run inside a delayed callback. */
    latest.current = {
        header,
        item,
        supplier,
        locked,
        calc,
        showSalePer,
        showCustPrice,
    };

    // fields that must be skipped right now (locked after save, supplier already chosen)
    const skipField = (key, s) =>
        (s.locked && LOCKED_KEYS.includes(key)) ||
        (key === "supplierBrowse" && !!s.supplier);

    // move the cursor to the field after `from` in ORDER (idempotent – safe to call twice)
    const go = (from) => {
        const s = latest.current;
        let i = ORDER.indexOf(from) + 1;
        while (i < ORDER.length && skipField(ORDER[i], s)) i += 1;
        if (i < ORDER.length) focusField(ORDER[i]);
    };

    // Enter pressed on `key`: mandatory field empty → warn and stay, otherwise go on.
    // opts.open = an AutoComplete dropdown is open: Enter may be choosing a list item
    // (state not updated yet), so the check is repeated a moment later.
    const advance = (key, opts = {}) => {
        const s = latest.current;
        const rule = REQ[key];

        if (rule && !rule[1](s)) {
            const warn = () => {
                messageApi.warning(`${rule[0]} is required`);
                focusField(key, 0);
            };
            if (opts.open) {
                setTimeout(() => {
                    if (!REQ[key][1](latest.current)) warn();
                }, 90);
            } else {
                warn();
            }
            return;
        }

        // invoice no is filled but the supplier is still missing → back to Browse
        if (key === "invoiceNo" && !s.supplier) {
            messageApi.warning("Select Supplier first");
            focusField("supplierBrowse", 0);
            return;
        }
        // all calculated boxes (Amount, Dir Price, Gross Amt, Dis Amt, Offer Price) must have a value
        if (key === "disPer" && !(s.calc.grossAmt > 0 && s.calc.dirPrice > 0 && s.calc.offerPrice > 0)) {
            messageApi.warning("Amount, Dir Price, Gross Amt and Offer Price must have a value");
            focusField(num(s.item.qty) > 0 ? (num(s.item.grossDp) > 0 ? "custPrice" : "grossDp") : "qty", 0);
            return;
        }
        go(key);
    };

    // onKeyDown for plain Inputs / DatePickers
    const onEnter = (key) => (e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        advance(key);
    };

    // props shared by every AutoComplete: ref, Enter key, open-state tracking, list pick → next field.
    // masterKey = the master table behind this dropdown; it is fetched the first time the field is focused.
    const autoProps = (key, onPick, masterKey) => ({
        inputRef: reg(key),
        onFocus: masterKey ? () => ensureMaster(masterKey) : undefined,
        onKeyDown: (e) => {
            if (e.key !== "Enter") return;
            advance(key, { open: !!openMap.current[key] });
        },
        onOpen: (o) => {
            openMap.current[key] = o;
        },
        onSelect: (v) => {
            if (onPick) onPick(v);
            go(key); // value came from the list: no empty-check needed
        },
    });

    const makePayload = (list) =>
        buildPurchasePayload({
            header,
            supplier,
            entryNo,
            entryDate: savedEntryDate,
            rows: list,
            totals: calcTotals(list, footer),
            footer,
        });

    /* ══════════ CLEAR EVERYTHING ══════════
       Reset / Cancel   → handleReset()       purchase type is kept (quick repeat entry)
       Final Save/Update → handleReset(false)  purchase type is cleared too, so the user
                                               picks it again and the entry no is read fresh
       maxSeq goes back to null and is read again (for the purchase type) by the Entry No
       effect as soon as the new entry no is created.
       Loaded masters stay cached – they are not fetched again.
       Cursor: purchase type kept → Browse supplier, cleared → Purchase Type. */
    const handleReset = (keepType = true) => {
        editingRef.current = false; // a new entry no must be fetched again
        focusInvoiceAfterBrowse.current = false;
        setHeader((p) => emptyHeader(keepType ? p : {}));
        setItem(emptyItem());
        setRows([]);
        setFooter(emptyFooter());
        setEntryNo("");
        setEditIndex(null);
        setSelectedSupplier(null);
        setBrowseCity(null);
        setBrowseText("");
        setInvoiceSaved(false);
        setStockSaved(false);
        setSavedEntryDate(null);
        setSavedTotals(null);
        setSeqUsed(0);
        clearListFilters();
        setDesignBase((p) => ({ ...p, maxSeq: null })); // re-read for the new entry no
        setEntryTick((t) => t + 1); // entry no must be fetched again
        focusField(keepType ? "supplierBrowse" : "purchaseType", 120);
    };

    /* ══════════ ADD / UPDATE ITEM ══════════
       SAVE   (PurchInvoice/Save)   : every NEW item of a NEW invoice – only that item's
                                      line is sent (SAVE_EACH_NEW_ITEM in the data file)
       UPDATE (PurchInvoice/Update) : only when an existing item is being EDITED (Edit button
                                      in the table) or the invoice was opened from the
                                      saved list – all lines are sent
       CurrentStockApi is NOT called here – only from the final button.
       Mandatory item fields are checked with the same REQ list the Enter key uses;
       the cursor jumps to the first empty one. Dis (%) is optional: empty = 0.
       GST % saved on the row = the GST actually used (auto from the purchase type, or typed).

       AFTER A SUCCESSFUL SAVE:
         kept    → the whole "Item details" block (Brand Code … Offer), the
                   "Quantity" block (Qty, Free) and the "Purchase Values"
                   inputs (Gross DP, + %, PurDis %, GST)
         cleared → Wholesale %, Sale %, Cust Price, Dis %
         cursor  → Brand Code */
    const handleAddItem = async () => {
        if (saving) return;
        if (!header.purchaseType) {
            focusField("purchaseType", 0);
            return messageApi.error("Select Purchase Type first");
        }
        if (!supplier) {
            focusField("supplierBrowse", 0);
            return messageApi.error("Select Supplier first");
        }
        if (!header.invoiceNo.trim()) {
            focusField("invoiceNo", 0);
            return messageApi.error("Enter Invoice No");
        }
        if (!entryNo) return messageApi.error("Entry No is not ready yet");

        const missing = findMissingItemField(latest.current);
        if (missing) {
            focusField(missing, 0);
            return messageApi.error(`${REQ[missing][0]} is required`);
        }
        if (!(calc.grossAmt > 0 && calc.dirPrice > 0 && calc.offerPrice > 0)) {
            focusField("custPrice", 0);
            return messageApi.error(
                "Amount, Dir Price, Gross Amt and Offer Price must have a value",
            );
        }

        // price code needs CODE_CONFIGURE – make sure it is loaded even if Gross DP was never focused
        const cm = buildCodeMap(await ensureMaster("codeConfigure"));
        const rowPriceCode = priceCode || buildPriceCode(calc.dirPrice, cm);
        if (!rowPriceCode) {
            focusField("grossDp", 0);
            return messageApi.error("Price code is not ready – check the CODE_CONFIGURE table");
        }

        const editing = editIndex !== null;
        const keys = editing
            ? {
                designCode: rows[editIndex].designCode,
                maxnum: rows[editIndex].maxnum,
                terminalid: rows[editIndex].terminalid,
            }
            : newRowKeys(pcode, designBase, seqUsed);
        if (!keys.designCode) {
            return messageApi.error("Design code is not ready – check the purchase type code");
        }

        const newRow = {
            ...item,
            gstPer: showGst, // the GST actually used for the calculation
            brandCode: item.brandCode === "" ? "0" : item.brandCode,
            disPer: item.disPer === "" ? "0" : item.disPer, // optional field → saved as 0
            ...calc,
            priceCode: rowPriceCode,
            ...keys,
        };
        const nextRows = editing
            ? rows.map((r, i) => (i === editIndex ? newRow : r))
            : [...rows, newRow];

        // Update only for editing an item / working on an invoice opened from the list.
        // (If SAVE_EACH_NEW_ITEM is switched off, items after the first one also Update.)
        const useUpdate = editing || stockSaved || (!SAVE_EACH_NEW_ITEM && invoiceSaved);

        setSaving(true);
        try {
            if (useUpdate) {
                await updatePurchInvoice(tenantName, makePayload(nextRows));
            } else {
                // Save sends only the item that was just added
                await savePurchInvoice(tenantName, makePayload([newRow]));
            }

            setInvoiceSaved(true);
            setSavedTotals(null); // rows changed → live totals from now on
            setRows(nextRows);
            if (editing) setEditIndex(null);
            else setSeqUsed((n) => n + 1);

            // keep the whole Item details block + Quantity + the Purchase Values inputs,
            // clear everything else (Wholesale, Sale values, Dis %)
            setItem({
                ...emptyItem(),
                // Item details block – kept
                brandCode: item.brandCode,
                brandName: item.brandName,
                itemCategory: item.itemCategory,
                itemName: item.itemName,
                hsn: item.hsn,
                size: item.size,
                colour: item.colour,
                fitting: item.fitting,
                offer: item.offer,
                // Quantity block – kept
                qty: item.qty,
                free: item.free,
                // Purchase Values block – kept (GST value + mode, so the same GST carries to the next item)
                grossDp: item.grossDp,
                plusPer: item.plusPer,
                purDisPer: item.purDisPer,
                gstPer: item.gstPer,
                gstMode: item.gstMode,
            });
            messageApi.success(editing ? "Item updated" : "Item added");
            // next item: cursor goes back to Brand Code (its text is selected, so typing replaces it)
            focusField("brandCode", 120);
        } catch (e) {
            console.error("Add item failed", e);
            messageApi.error(apiError(e, "Could not save the item"));
        } finally {
            setSaving(false);
        }
    };

    // load a table row back into the entry form
    // (GST of an existing row stays as saved until Gross DP is edited → gstMode "manual")
    const handleEditRow = (index) => {
        const row = rows[index];
        const restored = Object.keys(emptyItem()).reduce(
            (a, k) => ({ ...a, [k]: row[k] ?? "" }),
            {},
        );
        setItem({ ...restored, gstMode: "manual" });
        setEditIndex(index);
        focusField("itemName", 120);
    };

    /* ══════════ DELETE ROW ══════════
       other rows left  → PurchInvoice/Update with the remaining rows
       last row removed → Delete API (removes the whole invoice);
                          if the invoice was already in TRANS_ENTRY_DATA
                          that entry is deleted first, and the screen is cleared */
    const handleDeleteRow = async (index) => {
        if (saving) return;
        const remaining = rows.filter((_, i) => i !== index);
        setSaving(true);
        try {
            if (remaining.length === 0) {
                const key = supplierKey(header, supplier);
                const wasFinal = stockSaved;
                if (wasFinal) {
                    await deleteCurrentStock(tenantName, key);
                    setStockSaved(false); // so a retry does not call it again
                }
                await deletePurchInvoice(tenantName, key);
                setInvoiceSaved(false);
                if (wasFinal) {
                    handleReset();
                    messageApi.success("Invoice deleted");
                    return;
                }
            } else {
                await updatePurchInvoice(tenantName, makePayload(remaining));
            }
            setRows(remaining);
            setSavedTotals(null);
            setEditIndex((cur) => {
                if (cur === null) return null;
                if (cur === index) {
                    setItem(emptyItem());
                    return null;
                }
                return cur > index ? cur - 1 : cur;
            });
            messageApi.success("Item removed");
        } catch (e) {
            console.error("Delete failed", e);
            messageApi.error(apiError(e, "Could not remove the item"));
        } finally {
            setSaving(false);
        }
    };

    // reset buttons: warn when items are already saved on the server
    const requestReset = () => {
        if (!invoiceSaved) return handleReset();
        Modal.confirm({
            title: "Clear the screen?",
            content:
                "The items are already saved in the invoice. This only clears the form, it does not delete anything.",
            okText: "Clear",
            onOk: () => handleReset(),
        });
    };

    /* ══════════ SAVE / UPDATE (final button) ══════════
       Items are already saved by Add Item. This button:
         1) PurchInvoice/Update  – final totals (trade discount, charges, narration, header)
         2) CurrentStockApi/Save   (new invoice)   or
            CurrentStockApi/Update (loaded invoice)
       Then the whole screen is cleared INCLUDING the purchase type.
       If step 2 fails, step 1 is already done – press the button again, both are safe to repeat. */
    const handleFinish = async (print) => {
        if (saving) return;
        if (!invoiceSaved || !rows.length) {
            return messageApi.error("Add at least one item first");
        }
        if (editIndex !== null) {
            return messageApi.warning("Finish or cancel the item you are editing");
        }
        setSaving(true);
        try {
            await updatePurchInvoice(tenantName, makePayload(rows));

            const stockPayload = buildCurrentStockPayload({
                header,
                supplier,
                entryNo,
                entryDate: savedEntryDate,
                totals,
                footer,
            });
            if (stockSaved) await updateCurrentStock(tenantName, stockPayload);
            else await saveCurrentStock(tenantName, stockPayload);

            messageApi.success(
                `Purchase invoice ${entryNo} ${stockSaved ? "updated" : "saved"}`,
            );
            if (print) messageApi.info("Print will be added next");
            handleReset(false); // purchase type cleared too → user selects it again
        } catch (e) {
            console.error("Final save failed", e);
            messageApi.error(apiError(e, "Could not save the invoice"));
        } finally {
            setSaving(false);
        }
    };

    /* ══════════ SAVED INVOICES LIST (TRANS_ENTRY_DATA) ══════════ */
    const refreshList = async () => {
        setListLoading(true);
        try {
            // purchase types are normally loaded already (page open); this just reuses them
            const types = await ensureMaster("purchaseType");
            const typeNames = types.map((r) => clean(pick(r, FIELDS.purchaseType.name)));
            setListRows(await fetchPurchaseList(tenantName, typeNames));
        } catch (e) {
            console.error("Saved invoice list failed", e);
            setListRows([]);
            messageApi.error(apiError(e, "Could not load saved invoices"));
        } finally {
            setListLoading(false);
        }
    };

    const openList = () => {
        clearListFilters();
        setListOpen(true);
        refreshList();
    };

    // open a saved invoice:
    //   totals (qty / net amount) → the TRANS_ENTRY_DATA row that was clicked
    //   every item                → CURRENT_STOCK where ENTRYNO = the invoice
    //   last design sequence      → purchInvoice_DataDet where PType = this invoice's purchase type
    //   CODE_CONFIGURE            → loaded here (lazy) so each row gets its price code
    const loadInvoice = async (r) => {
        setOpeningNo(r.entryNo);
        try {
            const cm = buildCodeMap(await ensureMaster("codeConfigure"));
            const d = await loadSavedInvoice(tenantName, r, designBase, cm);

            editingRef.current = true; // keep THIS entry no (see the Entry No effect)
            setHeader({ ...emptyHeader(), ...d.header });
            setSelectedSupplier(d.supplier);
            setBrowseCity(d.header.city);
            setBrowseText("");
            setRows(d.rows);
            setFooter({ ...emptyFooter(), ...d.footer });
            setEntryNo(String(r.entryNo));
            setSavedEntryDate(d.entryDate);
            setSavedTotals(d.totals); // Total Qty + Net Amount straight from TRANS_ENTRY_DATA
            setItem(emptyItem());
            setEditIndex(null);
            setInvoiceSaved(true);
            setStockSaved(true); // item calls = Update, final button = CurrentStockApi/Update
            setSeqUsed(0);
            // new items continue after the highest MAXNUM of this invoice's purchase type
            loadMaxSeq(d.header.purchaseType);
            setListOpen(false);
            messageApi.success(`Invoice ${r.billNo || r.entryNo} opened`);
            if (d.masterMissing) {
                messageApi.warning(
                    "Transport / charges of this invoice could not be read – check them before updating",
                );
            }
            // header is locked on an opened invoice → cursor goes straight to the item entry
            focusField("brandName", 400);
        } catch (e) {
            console.error("Open invoice failed", e);
            messageApi.error(apiError(e, "Could not open the invoice"));
        } finally {
            setOpeningNo(null);
        }
    };

    // click a row in the list = open that invoice
    const pickInvoice = (r) => {
        if (openingNo !== null) return;
        if (rows.length === 0) return loadInvoice(r);
        Modal.confirm({
            title: "Open this invoice?",
            content:
                "The items on the screen are already saved. This only replaces what is shown.",
            okText: "Open",
            onOk: () => loadInvoice(r),
        });
    };

    /* ── table columns: wider, # sticky left, Action sticky right ── */
    const columns = [
        {
            title: "#",
            width: 60,
            align: "center",
            fixed: "left",
            render: (_, __, i) => i + 1,
        },
        {
            title: "Design Code",
            dataIndex: "designCode",
            width: 160,
            render: (v) => <strong style={{ color: "#c0392b" }}>{v || "—"}</strong>,
        },
        { title: "Brand Code", dataIndex: "brandCode", width: 120, align: "center" },
        { title: "Brand", dataIndex: "brandName", width: 180 },
        { title: "Item Category", dataIndex: "itemCategory", width: 180 },
        { title: "Item Name", dataIndex: "itemName", width: 210 },
        { title: "Size", dataIndex: "size", width: 100 },
        { title: "Color", dataIndex: "colour", width: 120 },
        { title: "Fashion", dataIndex: "fitting", width: 120 },
        { title: "Offer", dataIndex: "offer", width: 120 },
        { title: "Qty", dataIndex: "qty", align: "right", width: 100 },
        { title: "Free", dataIndex: "free", align: "right", width: 100 },
        {
            title: "Dir Price",
            dataIndex: "dirPrice",
            align: "right",
            width: 130,
            render: money,
        },
        {
            title: "Gross Amt",
            dataIndex: "grossAmt",
            align: "right",
            width: 140,
            render: (v) => <strong>{money(v)}</strong>,
        },
        {
            title: "Sale(%)",
            dataIndex: "salePer",
            align: "right",
            width: 110,
            render: money,
        },
        {
            title: "Cust Price",
            dataIndex: "custPrice",
            align: "right",
            width: 130,
            render: money,
        },
        { title: "Disc %", dataIndex: "disPer", align: "right", width: 110 },
        {
            title: "Offer Price",
            dataIndex: "offerPrice",
            align: "right",
            width: 140,
            render: money,
        },
        { title: "GST %", dataIndex: "gstPer", align: "right", width: 110 },
        { title: "HSN", dataIndex: "hsn", width: 120 },
        { title: "W %", dataIndex: "wPer", align: "right", width: 100 },
        {
            title: "W Price",
            dataIndex: "wPrice",
            align: "right",
            width: 130,
            render: money,
        },
        {
            title: "Action",
            key: "action",
            width: 100,
            align: "center",
            fixed: "right",
            render: (_, __, index) => (
                <div style={{ display: "flex", gap: 2, justifyContent: "center" }}>
                    <Button
                        size="small"
                        type="text"
                        icon={<EditOutlined />}
                        disabled={saving}
                        onClick={() => handleEditRow(index)}
                        style={{ color: "#4f46e5" }}
                    />
                    <Popconfirm
                        title={
                            rows.length === 1
                                ? "This is the last item – the whole invoice will be deleted. Continue?"
                                : "Remove this item?"
                        }
                        onConfirm={() => handleDeleteRow(index)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            size="small"
                            type="text"
                            danger
                            disabled={saving}
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </div>
            ),
        },
    ];

    const roStyle = { ...S.readonly };
    const numRight = { textAlign: "right" };
    const today = dayjs();

    // supplier browse dialog columns (avatar + name, short pill, GSTIN chip, station tag, arrow)
    const browseColumns = [
        {
            title: "Supplier",
            render: (_, r) => {
                const name = clean(pick(r, FIELDS.supplier.name));
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <div
                            style={{
                                width: 30,
                                height: 30,
                                flexShrink: 0,
                                borderRadius: 9,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 800,
                                fontSize: 13,
                                color: "#fff",
                                background: avatarColor(name),
                                boxShadow: `0 3px 8px ${avatarColor(name)}55`,
                            }}
                        >
                            {(name || "?").charAt(0).toUpperCase()}
                        </div>
                        <span
                            title={name}
                            style={{
                                fontWeight: 700,
                                color: "#0f172a",
                                fontSize: 13,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {name || "—"}
                        </span>
                    </div>
                );
            },
        },
        {
            title: "Short Name",
            width: 120,
            render: (_, r) => {
                const v = clean(pick(r, FIELDS.supplier.shortName));
                return v ? (
                    <span
                        style={{
                            background: "#f1f5f9",
                            border: "1px solid #e2e8f0",
                            color: "#475569",
                            borderRadius: 20,
                            padding: "1px 10px",
                            fontSize: 12,
                            fontWeight: 700,
                        }}
                    >
                        {v}
                    </span>
                ) : (
                    <span style={{ color: "#cbd5e1" }}>—</span>
                );
            },
        },
        {
            title: "GSTIN",
            width: 185,
            render: (_, r) => {
                const v = clean(pick(r, FIELDS.supplier.gstin));
                return v ? (
                    <span
                        style={{
                            background: "#fffbeb",
                            border: "1px solid #fde68a",
                            color: "#b45309",
                            borderRadius: 6,
                            padding: "1px 8px",
                            fontFamily: "monospace",
                            fontSize: 12,
                            fontWeight: 700,
                        }}
                    >
                        {v}
                    </span>
                ) : (
                    <span style={{ color: "#cbd5e1" }}>—</span>
                );
            },
        },
        {
            title: "Station",
            width: 105,
            render: (_, r) => {
                const st =
                    clean(pick(r, FIELDS.supplier.station)).toUpperCase() || "LOCAL";
                return (
                    <Tag
                        color={st === "OUTSTATION" ? "orange" : "blue"}
                        style={{ margin: 0, fontSize: 10, fontWeight: 700 }}
                    >
                        {st}
                    </Tag>
                );
            },
        },
        {
            title: "",
            width: 36,
            align: "center",
            render: () => <RightOutlined className="pi-sup-go" />,
        },
    ];

    // saved-invoices dialog columns (entry no chip, TYPE beside it, date, invoice no, supplier, qty, amount, arrow)
    const listColumns = [
        {
            title: "Entry No",
            width: 110,
            render: (_, r) => (
                <span
                    style={{
                        background: "#fffbeb",
                        border: "1px solid #fde68a",
                        color: "#b45309",
                        borderRadius: 6,
                        padding: "1px 8px",
                        fontFamily: "monospace",
                        fontSize: 12.5,
                        fontWeight: 800,
                    }}
                >
                    {r.entryNo}
                </span>
            ),
        },
        {
            title: "Type",
            width: 125,
            render: (_, r) => (
                <Tag color="purple" style={{ margin: 0, fontSize: 10, fontWeight: 700 }}>
                    {r.type || "—"}
                </Tag>
            ),
        },
        {
            title: "Date",
            width: 110,
            render: (_, r) => (
                <span style={{ fontSize: 12.5, color: "#475569", fontWeight: 600 }}>
                    {r.billDate ? dayjs(r.billDate).format("DD-MMM-YYYY") : "—"}
                </span>
            ),
        },
        {
            title: "Invoice No",
            width: 105,
            render: (_, r) => (
                <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 13 }}>
                    {r.billNo || "—"}
                </span>
            ),
        },
        {
            title: "Supplier",
            render: (_, r) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <div
                        style={{
                            width: 30,
                            height: 30,
                            flexShrink: 0,
                            borderRadius: 9,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 800,
                            fontSize: 13,
                            color: "#fff",
                            background: avatarColor(r.party),
                            boxShadow: `0 3px 8px ${avatarColor(r.party)}55`,
                        }}
                    >
                        {(r.party || "?").charAt(0).toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0, lineHeight: 1.2 }}>
                        <div
                            title={r.party}
                            style={{
                                fontWeight: 700,
                                color: "#0f172a",
                                fontSize: 13,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {r.party || "—"}
                        </div>
                        <div style={{ fontSize: 11, color: "#64748b" }}>{r.area}</div>
                    </div>
                </div>
            ),
        },
        {
            title: "Qty",
            width: 70,
            align: "right",
            render: (_, r) => <strong>{r.qty}</strong>,
        },
        {
            title: "Amount",
            width: 120,
            align: "right",
            render: (_, r) => (
                <strong style={{ color: "#047857" }}>{money(r.amount)}</strong>
            ),
        },
        {
            title: "",
            width: 36,
            align: "center",
            render: () => <RightOutlined className="pi-sup-go" />,
        },
    ];

    return (
        <>
            <style>{TABLE_CSS}</style>
            {contextHolder}
            <div
                className="pi-root"
                style={{
                    padding: 5,
                    display: "flex",
                    gap: 8,
                    height: "88vh",
                    overflow: "hidden", // no scrolling anywhere on this screen
                }}
            >
                {/* ══════════════════════════════════════════════
                    LEFT PANEL (50%) – strip + supplier/invoice + item entry
                   ══════════════════════════════════════════════ */}
                <div
                    className="pi-left"
                    style={{
                        flex: "1 1 0", // 50 / 50 with the right panel
                        minWidth: 0,
                        minHeight: 0, // lets the panel shrink so its own scrollbar works
                        position: "relative", // for the "select purchase type" hint
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                        overflowX: "hidden",
                        overflowY: "hidden", // NO scroll in the left block – inputs are kept short instead
                        padding: 8,
                        borderRadius: 20,
                        background: "var(--pi-bg)", // flat tone so the white cards + notches pop
                    }}
                >
                    {/* ── TOP STRIP: indigo bar · purchase type | entry no ticket | date leaf | saved list + reset ── */}
                    <div
                        className="pi-strip"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                            padding: "6px 14px",
                            borderRadius: 16,
                            background:
                                "linear-gradient(120deg,#1e1b4b 0%,#4338ca 55%,#7c3aed 100%)",
                            border: "1px solid rgba(255,255,255,0.15)",
                            boxShadow: "0 6px 18px rgba(67,56,202,0.35)",
                            flexShrink: 0,
                        }}
                    >
                        {/* purchase type – icon tile + small label over the select */}
                        <div
                            style={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                            }}
                        >
                            <div
                                style={{
                                    width: 36,
                                    height: 36,
                                    flexShrink: 0,
                                    borderRadius: 11,
                                    background: "rgba(255,255,255,0.18)",
                                    border: "1px solid rgba(255,255,255,0.35)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#fff",
                                    fontSize: 17,
                                }}
                            >
                                <FileDoneOutlined />
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    minWidth: 0,
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: "0.62rem",
                                        fontWeight: 800,
                                        letterSpacing: 1.3,
                                        color: "rgba(255,255,255,0.8)",
                                    }}
                                >
                                    PURCHASE TYPE
                                </span>
                                <div style={{ width: 250 }}>
                                    {/* Enter flow: first field. Enter opens the list, Enter again picks
                                        the highlighted type and the cursor moves on */}
                                    <Sel
                                        inputRef={reg("purchaseType")}
                                        value={header.purchaseType}
                                        onChange={(v) => {
                                            setH("purchaseType", v);
                                            // new type → GST is worked out again from that type's rule
                                            setItem((p) => ({ ...p, gstMode: "auto", gstPer: "" }));
                                        }}
                                        onSelect={() => go("purchaseType")}
                                        options={opts.purchaseType}
                                        placeholder="Select type"
                                        disabled={locked}
                                        loading={!!masterLoading.purchaseType}
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            style={{
                                filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
                                cursor: "help",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    padding: "5px 20px",
                                    color: "#78350f",
                                    background:
                                        "linear-gradient(135deg,#fde68a 0%,#f59e0b 100%)",
                                    WebkitMask: TICKET_MASK,
                                    mask: TICKET_MASK,
                                }}
                            >
                                <NumberOutlined style={{ fontSize: 18 }} />
                                <span
                                    style={{
                                        height: 30,
                                        borderLeft: "2px dashed rgba(120,53,15,0.35)",
                                    }}
                                />
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        lineHeight: 1.1,
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: "0.6rem",
                                            fontWeight: 800,
                                            letterSpacing: 1.3,
                                        }}
                                    >
                                        ENTRY NO
                                    </span>
                                    <span
                                        style={{
                                            fontSize: 20,
                                            fontWeight: 800,
                                            fontFamily: "monospace",
                                            letterSpacing: 1.5,
                                        }}
                                    >
                                        {entryNo || "—"}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* entry date – calendar leaf + weekday and full date */}
                        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                            <div
                                style={{
                                    width: 44,
                                    borderRadius: 9,
                                    overflow: "hidden",
                                    background: "#fff",
                                    boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
                                    textAlign: "center",
                                    lineHeight: 1,
                                }}
                            >
                                <div
                                    style={{
                                        background: "linear-gradient(135deg,#f43f5e,#dc2626)",
                                        color: "#fff",
                                        fontSize: 9,
                                        fontWeight: 800,
                                        letterSpacing: 1.2,
                                        padding: "3px 0",
                                    }}
                                >
                                    {today.format("MMM").toUpperCase()}
                                </div>
                                <div
                                    style={{
                                        fontSize: 21,
                                        fontWeight: 800,
                                        color: "#1e1b4b",
                                        padding: "3px 0 4px",
                                    }}
                                >
                                    {today.format("DD")}
                                </div>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    lineHeight: 1.2,
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: "0.6rem",
                                        fontWeight: 800,
                                        letterSpacing: 1.3,
                                        color: "rgba(255,255,255,0.7)",
                                    }}
                                >
                                    ENTRY DATE
                                </span>
                                <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>
                                    {today.format("dddd")}
                                </span>
                                <span style={{ color: "rgba(255,255,255,0.75)", fontSize: 11 }}>
                                    {today.format("DD-MMM-YYYY")}
                                </span>
                            </div>
                        </div>

                        {/* saved invoices list + reset – glass round buttons side by side */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <Tooltip title="Saved purchase invoices">
                                <button
                                    type="button"
                                    className="pi-reset pi-list"
                                    onClick={openList}
                                >
                                    <FileSearchOutlined />
                                </button>
                            </Tooltip>
                            <Tooltip title="Reset">
                                <button type="button" className="pi-reset" onClick={requestReset}>
                                    <ReloadOutlined />
                                </button>
                            </Tooltip>
                        </div>
                    </div>

                    {/* hint shown while the blocks below are disabled */}
                    {!typeReady && (
                        <div className="pi-gate-hint">
                            <FileDoneOutlined /> Select a Purchase Type to start
                        </div>
                    )}

                    {/* ════ EVERYTHING BELOW THE STRIP: disabled until a purchase type is selected ════ */}
                    <fieldset className="pi-gate" disabled={!typeReady}>
                        {/* ── TWO TICKET CARDS: supplier | invoice (kept short, tear lines line up) ── */}
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 10,
                                flexShrink: 0,
                            }}
                        >
                            {/* Supplier card – blue theme; title + edit/delete + Browse; SHORT/PAN/GSTIN in the stub */}
                            <Card
                                className="pi-supplier-card"
                                title="Supplier details"
                                icon={<ShopOutlined />}
                                accent={ACCENT.supplier}
                                extra={
                                    <>
                                        {supplier && (
                                            <>
                                                <button
                                                    type="button"
                                                    title="Edit supplier"
                                                    onClick={handleSupplierEdit}
                                                    style={iconBtn("#4f46e5")}
                                                >
                                                    <EditOutlined />
                                                </button>
                                                <Popconfirm
                                                    title="Delete this supplier?"
                                                    okText="Yes"
                                                    cancelText="No"
                                                    onConfirm={handleSupplierDelete}
                                                >
                                                    <button
                                                        type="button"
                                                        title="Delete supplier"
                                                        style={iconBtn("#dc2626")}
                                                    >
                                                        <DeleteOutlined />
                                                    </button>
                                                </Popconfirm>
                                            </>
                                        )}
                                        {/* Enter flow: after Purchase Type the cursor lands here – press Enter to open the dialog */}
                                        <button
                                            type="button"
                                            ref={reg("supplierBrowse")}
                                            className="pi-browse"
                                            onClick={openBrowse}
                                            disabled={locked || !typeReady}
                                        >
                                            <SearchOutlined /> Browse
                                        </button>
                                    </>
                                }
                                footer={
                                    <div style={{ display: "flex", gap: 5 }}>
                                        <Tile
                                            label="SHORT"
                                            value={supplierShort}
                                            bg="#fff"
                                            border="#c7d2fe"
                                            labelColor="#64748b"
                                            flex={0.8}
                                        />
                                        <Tile
                                            label="PAN"
                                            value={clean(pick(supplier, FIELDS.supplier.pan))}
                                            bg="#fff"
                                            border="#bfdbfe"
                                            labelColor="#1d4ed8"
                                            flex={1}
                                        />
                                        <Tile
                                            label="GSTIN"
                                            value={clean(pick(supplier, FIELDS.supplier.gstin))}
                                            bg="#fffbeb"
                                            border="#fde68a"
                                            labelColor="#b45309"
                                            flex={1.4}
                                        />
                                    </div>
                                }
                            >
                                {!supplier ? (
                                    <div
                                        style={{
                                            flex: 1,
                                            minHeight: 46,
                                            border: "1.5px dashed #a5b4fc",
                                            borderRadius: 10,
                                            background: "rgba(255,255,255,0.7)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 6,
                                            color: "#6366f1",
                                            fontSize: "0.78rem",
                                            fontWeight: 600,
                                        }}
                                    >
                                        <ShopOutlined style={{ fontSize: 16 }} />
                                        Click Browse to select supplier
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 4,
                                            flex: 1,
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <div
                                                style={{
                                                    width: 30,
                                                    height: 30,
                                                    flexShrink: 0,
                                                    borderRadius: 9,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontWeight: 800,
                                                    color: "#fff",
                                                    background: ACCENT.supplier,
                                                }}
                                            >
                                                {(supplierName || "?").charAt(0).toUpperCase()}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
                                                <div
                                                    title={supplierName}
                                                    style={{
                                                        color: "#0f172a",
                                                        fontWeight: 800,
                                                        fontSize: "0.88rem",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {supplierName}
                                                </div>
                                                <div
                                                    style={{
                                                        color: "#64748b",
                                                        fontSize: "0.72rem",
                                                        fontWeight: 600,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {[supplierShort, header.city].filter(Boolean).join(" · ")}
                                                </div>
                                            </div>
                                            <Tag
                                                color={station === "OUTSTATION" ? "orange" : "blue"}
                                                style={{ margin: 0, fontSize: 10, lineHeight: "16px" }}
                                            >
                                                {station}
                                            </Tag>
                                        </div>
                                        {/* address is ONE line (full text on hover) so the card stays short */}
                                        <div
                                            title={supplierAddress}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 5,
                                                fontSize: "0.74rem",
                                                color: "#64748b",
                                            }}
                                        >
                                            <EnvironmentOutlined />
                                            <span
                                                style={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {supplierAddress || "-"}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </Card>

                            {/* Invoice card – 3 fields on top, 3 below the tear line (Transport button is one of them) */}
                            <Card
                                title="Invoice details"
                                icon={<FileTextOutlined />}
                                accent={ACCENT.invoice}
                                pill={
                                    stockSaved ? (
                                        <Pill bg="#fff1f2" color="#be123c">
                                            Editing
                                        </Pill>
                                    ) : null
                                }
                                footer={
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr 1fr",
                                            gap: "2px 6px",
                                        }}
                                    >
                                        <Fld label="Due Days">
                                            <Input
                                                ref={reg("dueDays")}
                                                value={header.dueDays}
                                                onChange={(e) =>
                                                    /^\d*$/.test(e.target.value) && setH("dueDays", e.target.value)
                                                }
                                                onKeyDown={onEnter("dueDays")}
                                            />
                                        </Fld>
                                        <Fld label="Margin %">
                                            <Input
                                                ref={reg("marginPer")}
                                                value={header.marginPer}
                                                onChange={(e) =>
                                                    /^\d*\.?\d{0,2}$/.test(e.target.value) &&
                                                    setH("marginPer", e.target.value)
                                                }
                                                onKeyDown={onEnter("marginPer")}
                                            />
                                        </Fld>
                                        <Fld label="Transport / LR">
                                            <Tooltip title={transportSummary}>
                                                <button
                                                    type="button"
                                                    className="pi-transport-btn"
                                                    onClick={openTransport}
                                                >
                                                    <span className="pi-car">
                                                        <CarOutlined />
                                                    </span>
                                                    TRANSPORT
                                                    {transportFilled && (
                                                        <CheckCircleFilled style={{ color: "#bbf7d0" }} />
                                                    )}
                                                </button>
                                            </Tooltip>
                                        </Fld>
                                    </div>
                                }
                            >
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr 1fr",
                                        gap: "2px 6px",
                                    }}
                                >
                                    <Fld label="Invoice No">
                                        <Input
                                            ref={reg("invoiceNo")}
                                            value={header.invoiceNo}
                                            disabled={locked}
                                            onChange={(e) => setH("invoiceNo", e.target.value)}
                                            onKeyDown={onEnter("invoiceNo")}
                                        />
                                    </Fld>
                                    <Fld label="Invoice Date">
                                        <DatePicker
                                            ref={reg("invoiceDate")}
                                            allowClear={false}
                                            suffixIcon={null}
                                            format="DD-MMM-YYYY"
                                            style={{ width: "100%" }}
                                            value={header.invoiceDate}
                                            onKeyDown={onEnter("invoiceDate")}
                                            onChange={(d) =>
                                                setHeader((p) => ({
                                                    ...p,
                                                    invoiceDate: d,
                                                    stockRecDate: d, // stock rec date follows invoice date
                                                }))
                                            }
                                        />
                                    </Fld>
                                    <Fld label="Stock Rec. Date">
                                        <DatePicker
                                            ref={reg("stockRecDate")}
                                            allowClear={false}
                                            suffixIcon={null}
                                            format="DD-MMM-YYYY"
                                            style={{ width: "100%" }}
                                            value={header.stockRecDate}
                                            onKeyDown={onEnter("stockRecDate")}
                                            onChange={(d) => setH("stockRecDate", d)}
                                        />
                                    </Fld>
                                </div>
                            </Card>
                        </div>

                        {/* ── ITEM BLOCKS: no outer "Item entry" card – each block is its own white card ── */}
                        <SubCard
                            title={editIndex !== null ? `Item details · editing #${editIndex + 1}` : "Item details"}
                            icon={<ShoppingOutlined />}
                            accent={ACCENT.item}
                            cols={24}
                        >
                            <Fld label="Brand Code" span={6} color="#b45309">
                                <Input
                                    ref={reg("brandCode")}
                                    className="pi-brand-input"
                                    placeholder="0"
                                    value={item.brandCode}
                                    onChange={(e) =>
                                        /^\d*$/.test(e.target.value) && setI("brandCode", e.target.value)
                                    }
                                    onKeyDown={onEnter("brandCode")}
                                    style={{ textAlign: "center" }}
                                />
                            </Fld>

                            <div style={{ gridColumn: "span 6", minWidth: 0 }}>
                                <Fld label="Design Code (item)" color="#4f46e5">
                                    <Input
                                        className="pi-design-input"
                                        readOnly
                                        tabIndex={-1}
                                        placeholder="Auto"
                                        value={nextDesignCode}
                                        style={{ textAlign: "center" }}
                                    />
                                </Fld>
                            </div>

                            <Fld label="Brand Name" span={6}>
                                <Auto
                                    value={item.brandName}
                                    onChange={(v) => setI("brandName", v)}
                                    options={opts.brand}
                                    placeholder="Select or type brand"
                                    {...autoProps("brandName", undefined, "company")}
                                />
                            </Fld>
                            <Fld label="Item Category" span={6}>
                                <Auto
                                    value={item.itemCategory}
                                    onChange={onCategoryChange}
                                    options={opts.category}
                                    placeholder="Select or type category"
                                    {...autoProps("itemCategory", onCategorySelect, "group")}
                                />
                            </Fld>

                            <Fld label="Item Name" span={9}>
                                <Auto
                                    value={item.itemName}
                                    onChange={(v) => setI("itemName", v)}
                                    options={itemOptions}
                                    placeholder="Select or type item"
                                    {...autoProps("itemName", undefined, "item")}
                                />
                            </Fld>
                            <Fld label="HSN Code" span={3}>
                                <Input
                                    ref={reg("hsn")}
                                    value={item.hsn}
                                    onChange={(e) => setI("hsn", e.target.value)}
                                    onKeyDown={onEnter("hsn")}
                                    style={{ textAlign: "center" }}
                                />
                            </Fld>
                            <Fld label="Size" span={6}>
                                <Auto
                                    value={item.size}
                                    onChange={(v) => setI("size", v)}
                                    options={opts.size}
                                    placeholder="Size"
                                    {...autoProps("size", undefined, "size")}
                                />
                            </Fld>
                            <Fld label="Color" span={6}>
                                <Auto
                                    value={item.colour}
                                    onChange={(v) => setI("colour", v)}
                                    options={opts.colour}
                                    placeholder="Color"
                                    {...autoProps("colour", undefined, "colour")}
                                />
                            </Fld>
                            <Fld label="Fashion" span={12}>
                                <Auto
                                    value={item.fitting}
                                    onChange={(v) => setI("fitting", v)}
                                    options={opts.fitting}
                                    placeholder="Fashion"
                                    {...autoProps("fitting", undefined, "fitting")}
                                />
                            </Fld>
                            <Fld label="Offer" span={12}>
                                <Auto
                                    value={item.offer}
                                    onChange={(v) => setI("offer", v)}
                                    options={opts.offer}
                                    placeholder="Offer"
                                    {...autoProps("offer", undefined, "sleeves")}
                                />
                            </Fld>
                        </SubCard>

                        {/* ROW 1: Quantity + Purchase values on ONE line */}
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "minmax(0,2fr) minmax(0,7.2fr)",
                                gap: 10,
                                flexShrink: 0,
                            }}
                        >
                            <SubCard
                                title="Quantity"
                                icon={<NumberOutlined />}
                                accent={ACCENT.qty}
                                cols={2}
                            >
                                <Fld label="Qty">
                                    <Input
                                        ref={reg("qty")}
                                        value={item.qty}
                                        onChange={(e) => setINum("qty", e.target.value)}
                                        onKeyDown={onEnter("qty")}
                                        style={{ fontWeight: 800, textAlign: "center" }}
                                    />
                                </Fld>
                                <Fld label="Free">
                                    <Input
                                        ref={reg("free")}
                                        value={item.free}
                                        onChange={(e) => setINum("free", e.target.value)}
                                        onKeyDown={onEnter("free")}
                                        style={{ textAlign: "center" }}
                                    />
                                </Fld>
                            </SubCard>

                            <SubCard
                                title="Purchase Values"
                                icon={<CalculatorOutlined />}
                                accent={ACCENT.purchase}
                                template="0.95fr 0.8fr 0.95fr 0.75fr 1.15fr 1.15fr 1.2fr"
                                extra={
                                    <>
                                        <span className="pi-code-chip" title="Dealer price code">
                                            <small>CODE</small>
                                            <b>{priceCode || "—"}</b>
                                        </span>
                                        <button
                                            type="button"
                                            className="pi-info-btn"
                                            tabIndex={-1}
                                            onClick={() => setInfoOpen(true)}
                                        >
                                            <InfoCircleOutlined /> More info
                                        </button>
                                    </>
                                }
                            >
                                {[
                                    ["Gross DP", "grossDp"],
                                    ["+ (%)", "plusPer"],
                                    ["PurDis(%)", "purDisPer"],
                                ].map(([l, k]) => (
                                    <Fld key={k} label={l}>
                                        <Input
                                            ref={reg(k)}
                                            value={item[k]}
                                            onChange={(e) =>
                                                k === "grossDp"
                                                    ? onGrossDp(e.target.value)
                                                    : setINum(k, e.target.value)
                                            }
                                            // price code needs CODE_CONFIGURE → fetched the first time Gross DP is used
                                            onFocus={
                                                k === "grossDp"
                                                    ? () => ensureMaster("codeConfigure")
                                                    : undefined
                                            }
                                            onKeyDown={onEnter(k)}
                                            style={numRight}
                                        />
                                    </Fld>
                                ))}
                                {/* GST fills itself from the purchase type + Gross DP; typing here overrides it */}
                                <Fld label="GST">
                                    <Input
                                        readOnly
                                        tabIndex={-1}
                                        value={showGst}
                                        placeholder={purchaseTypeRow && autoGst === null ? "?" : ""}
                                        style={roStyle}
                                    />
                                </Fld>
                                <Fld label="Amount">
                                    <Input readOnly tabIndex={-1} value={money(calc.amount)} style={roStyle} />
                                </Fld>
                                <Fld label="Dir Price">
                                    <Input readOnly tabIndex={-1} value={money(calc.dirPrice)} style={roStyle} />
                                </Fld>
                                <Fld label="Gross Amt">
                                    <Input readOnly tabIndex={-1} value={money(calc.grossAmt)} style={roStyle} />
                                </Fld>
                            </SubCard>
                        </div>

                        {/* ROW 2: Wholesale values + Sale values on ONE line (Token removed) */}
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "minmax(0,2fr) minmax(0,5.2fr)",
                                gap: 10,
                                flexShrink: 0,
                            }}
                        >
                            <SubCard
                                title="Wholesale Values"
                                icon={<ShopOutlined />}
                                accent={ACCENT.wholesale}
                                cols={2}
                            >
                                <Fld label="Wholesale(%)">
                                    <Input
                                        ref={reg("wPer")}
                                        value={item.wPer}
                                        onChange={(e) => setINum("wPer", e.target.value)}
                                        onKeyDown={onEnter("wPer")}
                                        style={numRight}
                                    />
                                </Fld>
                                <Fld label="WSale Price">
                                    <Input readOnly tabIndex={-1} value={money(calc.wPrice)} style={roStyle} />
                                </Fld>
                            </SubCard>

                            {/* Sale % and Cust Price drive each other */}
                            <SubCard
                                title="Sale Values"
                                icon={<PercentageOutlined />}
                                accent={ACCENT.sale}
                                template="0.9fr 1fr 0.8fr 1fr 1.1fr"
                            >
                                <Fld label="Sale(%)">
                                    <Input
                                        ref={reg("salePer")}
                                        value={showSalePer}
                                        onChange={(e) => onSalePer(e.target.value)}
                                        onKeyDown={onEnter("salePer")}
                                        style={numRight}
                                    />
                                </Fld>
                                <Fld label="Cust Price">
                                    <Input
                                        ref={reg("custPrice")}
                                        value={showCustPrice}
                                        onChange={(e) => onCustPrice(e.target.value)}
                                        onKeyDown={onEnter("custPrice")}
                                        style={numRight}
                                    />
                                </Fld>
                                {/* Dis (%) is optional: Enter skips it, empty is saved as 0 */}
                                <Fld label="Dis (%)">
                                    <Input
                                        ref={reg("disPer")}
                                        value={item.disPer}
                                        onChange={(e) => setINum("disPer", e.target.value)}
                                        onKeyDown={onEnter("disPer")}
                                        style={numRight}
                                    />
                                </Fld>
                                <Fld label="Dis Amt">
                                    <Input readOnly tabIndex={-1} value={money(calc.disRs)} style={roStyle} />
                                </Fld>
                                <Fld label="Offer Price">
                                    <Input readOnly tabIndex={-1} value={money(calc.offerPrice)} style={roStyle} />
                                </Fld>
                            </SubCard>
                        </div>

                        {/* BOTTOM ROW: sale round-off pill (opens its dialog) + Add / Update item */}
                        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                            <button
                                type="button"
                                className="pi-round-btn"
                                onClick={() => setRoundOpen(true)}
                            >
                                <span className="pi-gear">
                                    <ControlOutlined />
                                </span>
                                <span style={{ display: "flex", flexDirection: "column" }}>
                                    <span className="pi-round-title">SALE ROUND-OFF</span>
                                    <span className="pi-round-sum">
                                        {roundLabel}
                                        {footer.setFloor && " · Floor"}
                                        {footer.narration.trim() && " · Note"}
                                    </span>
                                </span>
                            </button>
                            {editIndex !== null && (
                                <Button
                                    onClick={() => {
                                        setEditIndex(null);
                                        setItem(emptyItem());
                                    }}
                                    style={{ height: 46, borderRadius: 12, fontWeight: 700 }}
                                >
                                    Cancel edit
                                </Button>
                            )}
                            {/* Enter flow: the cursor ends here – press Enter once more to add the item */}
                            <Button
                                ref={reg("addItem")}
                                type="primary"
                                loading={saving}
                                icon={editIndex !== null ? <EditOutlined /> : <PlusOutlined />}
                                onClick={handleAddItem}
                                style={{
                                    flex: 1,
                                    height: 46,
                                    borderRadius: 12,
                                    fontWeight: 800,
                                    border: "none",
                                    background: "linear-gradient(135deg,#7c3aed 0%,#c026d3 100%)",
                                    boxShadow: "0 6px 14px rgba(124,58,237,0.35)",
                                }}
                            >
                                {editIndex !== null ? "Update Item" : "Add Item"}
                            </Button>
                        </div>
                    </fieldset>
                </div>

                {/* ══════════════════════════════════════════════
                    RIGHT PANEL (50%) – table on top, round-off + totals + actions below
                   ══════════════════════════════════════════════ */}
                <div
                    style={{
                        flex: "1 1 0", // 50 / 50 with the left panel
                        minWidth: 0,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        padding: 8,
                        borderRadius: 20,
                        background: "var(--pi-bg)",
                    }}
                >
                    {/* ITEMS TABLE – "Added items" heading + fixed-height table (measured) */}
                    <div
                        ref={tableWrapRef}
                        className="pi-table"
                        style={{
                            "--pi-ty": `${tableY}px`,
                            flex: 1,
                            minHeight: 200,
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            background: "#fff",
                            borderRadius: 18,
                            overflow: "hidden",
                            boxShadow: "0 12px 30px rgba(15,23,42,0.10), 0 2px 6px rgba(15,23,42,0.06)",
                        }}
                    >
                        <span className="pi-hole" />
                        <div className="pi-table-head">
                            <span className="pi-card-icon" style={{ background: ACCENT.added }}>
                                <UnorderedListOutlined />
                            </span>
                            <span className="pi-card-title">Added items</span>
                            <span style={{ marginLeft: "auto" }}>
                                <Pill bg="#f1f5f9" color="#334155">
                                    {rows.length} item{rows.length === 1 ? "" : "s"}
                                </Pill>
                            </span>
                        </div>
                        <div style={{ flex: 1, minHeight: 0 }}>
                            <DashboardTableHeaderStyles saleCode={2}>
                                <Table
                                    columns={columns}
                                    dataSource={rows}
                                    pagination={false}
                                    size="small"
                                    rowKey={(_, i) => i}
                                    rowClassName={(_, i) => (i === editIndex ? "pi-row-edit" : "")}
                                    scroll={{ x: 2900, y: tableY }}
                                />
                            </DashboardTableHeaderStyles>
                        </div>
                    </div>

                    {/* FOOTER: amount details | total qty card + action buttons */}
                    <div
                        className="pi-foot"
                        style={{
                            display: "flex",
                            gap: 20,
                            flexShrink: 0,
                            alignItems: "stretch",
                        }}
                    >
                        {/* AMOUNT DETAILS – disabled (fieldset) until a purchase type is selected */}
                        <fieldset
                            className="pi-gate"
                            disabled={!typeReady}
                            style={{ flex: "0 0 550px", maxWidth: "62%" }}
                        >
                            <Card
                                title="Amount details"
                                icon={<CalculatorOutlined />}
                                accent={ACCENT.amount}
                                style={{ flex: 1 }}
                                bodyStyle={{ gap: 4 }}
                                footer={
                                    <div className="pi-net">
                                        <span>Net Amount</span>
                                        <b>{shownNet.toFixed(0)}</b>
                                    </div>
                                }
                            >
                                <LRow label="Total Amount" mark>
                                    <Input readOnly value={money(totals.totalAmount)} style={roStyle} />
                                </LRow>
                                <div className="pi-tri">
                                    <div className="pi-tri-c">
                                        <span className="pi-tri-l">
                                            <i style={{ background: "#0ea5e9" }} />
                                            + Value
                                        </span>
                                        <Input readOnly value={money(totals.plusValue)} style={roStyle} />
                                    </div>
                                    <div className="pi-tri-c">
                                        <span className="pi-tri-l">
                                            <i style={{ background: "#f59e0b" }} />
                                            Discount
                                        </span>
                                        <Input readOnly value={money(totals.discValue)} style={roStyle} />
                                    </div>
                                    <div className="pi-tri-c">
                                        <span className="pi-tri-l">
                                            <i style={{ background: "#16a34a" }} />
                                            GST
                                        </span>
                                        <Input readOnly value={money(totals.gstValue)} style={roStyle} />
                                    </div>
                                </div>
                                <LRow label="Gross Amount" mark>
                                    <Input readOnly value={money(totals.grossAmount)} style={roStyle} />
                                </LRow>
                                <LRow label="Other Additions">
                                    <Input
                                        value={footer.otherAdditions}
                                        onChange={(e) => setFNum("otherAdditions", e.target.value)}
                                        style={numRight}
                                    />
                                </LRow>
                                <LRow label="Trade Discount">
                                    <Input
                                        value={footer.tradeDisPer}
                                        onChange={(e) => setFNum("tradeDisPer", e.target.value)}
                                        style={numRight}
                                    />
                                    <span className="pi-mini">(%)</span>
                                    <Input readOnly value={money(totals.tradeDisAmt)} style={roStyle} />
                                </LRow>
                                <LRow label="Adjustment">
                                    <Input
                                        value={footer.adjustment}
                                        onChange={(e) => setFNum("adjustment", e.target.value)}
                                        style={numRight}
                                    />
                                </LRow>
                                <LRow label="Freight Chrgs">
                                    <Input
                                        value={footer.freight}
                                        onChange={(e) => setFNum("freight", e.target.value)}
                                        style={numRight}
                                    />
                                    <span className="pi-mini">Other Chrgs</span>
                                    <Input
                                        value={footer.otherChrgs}
                                        onChange={(e) => setFNum("otherChrgs", e.target.value)}
                                        style={numRight}
                                    />
                                </LRow>
                            </Card>
                        </fieldset>

                        {/* TOTAL QTY (half-height stat card) + the three action buttons, all in ONE style */}
                        <div
                            style={{
                                flex: "1 1 0",
                                minWidth: 120,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                gap: 4,
                            }}
                        >
                            <div className="pi-qty">
                                <span className="pi-qty-l">
                                    <TagOutlined /> TOTAL QTY
                                </span>
                                <b>{shownQty}</b>
                            </div>

                            <button
                                type="button"
                                className="pi-act pi-b-save"
                                disabled={saving || !typeReady}
                                onClick={() => handleFinish(false)}
                            >
                                <span className="pi-coin">
                                    <SaveOutlined />
                                </span>
                                <span className="pi-txt">{stockSaved ? "UPDATE" : "SAVE"}</span>
                            </button>

                            <button
                                type="button"
                                className="pi-act pi-b-print"
                                disabled={saving || !typeReady}
                                onClick={() => handleFinish(true)}
                            >
                                <span className="pi-coin">
                                    <PrinterOutlined />
                                </span>
                                <span className="pi-txt">
                                    {stockSaved ? "UPDATE & PRINT" : "SAVE & PRINT"}
                                </span>
                            </button>

                            <button
                                type="button"
                                className="pi-act pi-b-cancel"
                                onClick={requestReset}
                            >
                                <span className="pi-coin">
                                    <CloseOutlined />
                                </span>
                                <span className="pi-txt">CANCEL</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ═════════════ PURCHASE VALUES – MORE INFO DIALOG (full amount breakdown) ═════════════ */}
            <Modal
                title={null}
                open={infoOpen}
                onCancel={() => setInfoOpen(false)}
                width={460}
                wrapClassName="pi-modal"
                footer={[
                    <Button
                        key="close"
                        type="primary"
                        onClick={() => setInfoOpen(false)}
                        style={{
                            border: "none",
                            fontWeight: 700,
                            background: "linear-gradient(135deg,#f59e0b,#d97706)",
                        }}
                    >
                        Close
                    </Button>,
                ]}
            >
                <DialogHead
                    icon={<CalculatorOutlined />}
                    title="Amount Breakdown"
                    subtitle={
                        item.itemName.trim()
                            ? `${item.itemName.trim()} · how the amount is calculated`
                            : "How the purchase amount is calculated"
                    }
                    gradient="linear-gradient(135deg,#f59e0b 0%,#d97706 100%)"
                />
                <div
                    className="pi-dlg-body"
                    style={{ paddingBottom: 8, maxHeight: "70vh", overflowY: "auto" }}
                >
                    <div className="pi-info-hero">
                        <span>GROSS AMT (INCL. GST)</span>
                        <b>{money(calc.grossAmt)}</b>
                    </div>

                    {!infoHasData && (
                        <div className="pi-info-empty">
                            Enter Qty and Gross DP to see the full calculation
                        </div>
                    )}

                    <div className="pi-info-sec">Purchase breakdown</div>
                    <InfoRow
                        label="Gross DP"
                        hint="price per piece"
                        value={money(item.grossDp)}
                    />
                    <InfoRow
                        label="Quantity"
                        hint={num(item.free) > 0 ? `plus ${num(item.free)} free` : "pieces"}
                        value={num(item.qty)}
                    />
                    <InfoRow
                        label="Amount"
                        hint="Gross DP × Qty"
                        value={money(calc.amount)}
                        strong
                    />
                    <InfoRow
                        label={`+ Plus (${num(item.plusPer)}%)`}
                        hint="added on Amount"
                        value={`+ ${money(calc.plusValue)}`}
                        tone="#0369a1"
                    />
                    <InfoRow
                        label={`− Purchase discount (${num(item.purDisPer)}%)`}
                        hint="taken off Amount + Plus"
                        value={`− ${money(calc.discValue)}`}
                        tone="#b45309"
                    />
                    <InfoRow
                        label="Taxable amount"
                        hint="Amount + Plus − Discount"
                        value={money(taxableAmt)}
                        strong
                    />
                    <InfoRow
                        label={`GST (${gstPerNum}%)`}
                        hint={isOutStation ? "Outstation supplier → IGST" : "Local supplier → CGST + SGST"}
                        value={`+ ${money(calc.gstValue)}`}
                        tone="#15803d"
                    />
                    {isOutStation ? (
                        <InfoRow
                            sub
                            label={`IGST (${gstPerNum}%)`}
                            value={money(calc.gstValue)}
                        />
                    ) : (
                        <>
                            <InfoRow
                                sub
                                label={`CGST (${gstPerNum / 2}%)`}
                                value={money(calc.gstValue / 2)}
                            />
                            <InfoRow
                                sub
                                label={`SGST (${gstPerNum / 2}%)`}
                                value={money(calc.gstValue / 2)}
                            />
                        </>
                    )}
                    <InfoRow
                        label="Gross Amt"
                        hint="Taxable amount + GST"
                        value={money(calc.grossAmt)}
                        strong
                    />
                    <InfoRow
                        label="Dir Price"
                        hint="Gross Amt ÷ Qty (cost per piece)"
                        value={money(calc.dirPrice)}
                        strong
                    />

                    <div className="pi-info-sec">Selling prices</div>
                    <InfoRow
                        label={`Wholesale price (${num(item.wPer)}%)`}
                        hint="Dir Price + Wholesale %"
                        value={money(calc.wPrice)}
                    />
                    <InfoRow
                        label={`Cust Price (Sale ${num(calc.salePer).toFixed(2)}%)`}
                        hint="Dir Price + Sale %"
                        value={money(calc.custPrice)}
                    />
                    <InfoRow
                        label={`Dis Amt (${num(item.disPer)}%)`}
                        hint="discount on Cust Price"
                        value={`− ${money(calc.disRs)}`}
                        tone="#b45309"
                    />
                    <InfoRow
                        label="Offer Price"
                        hint="Cust Price − Dis Amt"
                        value={money(calc.offerPrice)}
                        strong
                    />
                </div>
            </Modal>

            {/* ═════════════ SALE ROUND-OFF DIALOG – blue header like its button ═════════════ */}
            <Modal
                title={null}
                open={roundOpen}
                onCancel={() => setRoundOpen(false)}
                width={440}
                wrapClassName="pi-modal"
                footer={[
                    <Button key="clear" onClick={clearRound}>
                        Clear
                    </Button>,
                    <Button
                        key="ok"
                        type="primary"
                        onClick={() => setRoundOpen(false)}
                        style={{
                            border: "none",
                            fontWeight: 700,
                            background: "linear-gradient(135deg,#ec4899,#f43f5e)",
                        }}
                    >
                        Done
                    </Button>,
                ]}
            >
                <DialogHead
                    icon={<ControlOutlined />}
                    title="Sale Price Round-off"
                    subtitle="Round-off style, floor and narration"
                    gradient="linear-gradient(135deg,#ec4899 0%,#f43f5e 55%,#fb7185 100%)"
                />
                <div
                    className="pi-dlg-body"
                    style={{ display: "flex", flexDirection: "column", gap: 12 }}
                >
                    <Fld label="Round-off">

                        <div className="pi-chip-grid">
                            {ROUND_OPTS.map((o) => (
                                <button
                                    key={o.value}
                                    type="button"
                                    className={`pi-chip${footer.roundoff === o.value ? " on" : ""}`}
                                    onClick={() => setF("roundoff", o.value)}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </Fld>
                    <Checkbox
                        checked={footer.setFloor}
                        onChange={(e) => setF("setFloor", e.target.checked)}
                    >
                        <span style={{ ...S.label, fontWeight: 700 }}>SET FLOOR</span>
                    </Checkbox>
                    <Fld label="Narration">
                        <Input.TextArea
                            rows={3}
                            value={footer.narration}
                            onChange={(e) => setF("narration", e.target.value)}
                            placeholder="Add a note for this invoice"
                        />
                    </Fld>
                </div>
            </Modal>

            {/* ═════════════ TRANSPORT MINI DIALOG – orange-pink header like its button ═════════════ */}
            <Modal
                title={null}
                open={transportOpen}
                onCancel={() => setTransportOpen(false)}
                width={400}
                wrapClassName="pi-modal"
                footer={[
                    <Button key="clear" onClick={clearTransport}>
                        Clear
                    </Button>,
                    <Button
                        key="ok"
                        type="primary"
                        onClick={() => setTransportOpen(false)}
                        style={{
                            border: "none",
                            fontWeight: 700,
                            background: "linear-gradient(135deg,#f97316 0%,#ec4899 100%)",
                        }}
                    >
                        Done
                    </Button>,
                ]}
            >
                <DialogHead
                    icon={<CarOutlined />}
                    title="Transport Details"
                    subtitle="Transport, L R No and L R Date"
                    gradient="linear-gradient(135deg,#f97316 0%,#ec4899 100%)"
                />
                <div
                    className="pi-dlg-body"
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                    <Fld label="Transport">
                        <Sel
                            value={header.transport}
                            onChange={(v) => setH("transport", v)}
                            options={opts.transport}
                            placeholder="Select transport"
                            loading={!!masterLoading.transport}
                        />
                    </Fld>
                    <Fld label="L R No">
                        <Input
                            value={header.lrNo}
                            onChange={(e) => setH("lrNo", e.target.value)}
                        />
                    </Fld>
                    <Fld label="L R Date">
                        <DatePicker
                            format="DD-MMM-YYYY"
                            style={{ width: "100%" }}
                            value={header.lrDate}
                            onChange={(d) => setH("lrDate", d)}
                        />
                    </Fld>
                </div>
            </Modal>

            {/* ═════════════ SUPPLIER BROWSE DIALOG – blue header like the Supplier card ═════════════
                keyboard: opens on City (or on Search when a city is already set) →
                pick city → cursor jumps to Search → Enter picks the first match →
                dialog closes → cursor lands on Invoice No */}
            <Modal
                title={null}
                open={browseOpen}
                onCancel={() => setBrowseOpen(false)}
                afterOpenChange={(o) => {
                    if (o) {
                        focusField(browseCity ? "browseText" : "browseCity", 0);
                    } else if (focusInvoiceAfterBrowse.current) {
                        // dialog is fully closed and has already handed focus back to the Browse
                        // button, so now move the cursor to Invoice No
                        focusInvoiceAfterBrowse.current = false;
                        focusField("invoiceNo", 30);
                    }
                }}
                footer={null}
                width={780}
                destroyOnClose
                wrapClassName="pi-modal"
            >
                <DialogHead
                    icon={<ShopOutlined />}
                    title="Browse Suppliers"
                    subtitle="Pick a city, then click a supplier to select it"
                    gradient="linear-gradient(135deg,#2563eb 0%,#4f46e5 100%)"
                />
                <div className="pi-dlg-body" style={{ paddingBottom: 16 }}>
                    {/* filters in a soft tinted panel */}
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1.4fr",
                            gap: 10,
                            padding: "8px 10px 10px",
                            marginBottom: 10,
                            borderRadius: 10,
                            background: "linear-gradient(135deg,#eff6ff 0%,#e0e7ff 100%)",
                            border: "1px solid #c7d2fe",
                        }}
                    >
                        <Fld label="City" color="#1d4ed8">
                            <Sel
                                inputRef={reg("browseCity")}
                                value={browseCity}
                                onChange={(v) => {
                                    setBrowseCity(v);
                                    setBrowseText("");
                                }}
                                onSelect={() => focusField("browseText")}
                                options={opts.city}
                                placeholder="Select city"
                                loading={!!masterLoading.area}
                            />
                        </Fld>
                        <Fld label="Supplier" color="#1d4ed8">
                            <Input
                                ref={reg("browseText")}
                                allowClear
                                prefix={<SearchOutlined style={{ color: "#2563eb" }} />}
                                placeholder="Search name / short name / GSTIN – Enter picks the first match"
                                value={browseText}
                                onChange={(e) => setBrowseText(e.target.value)}
                                onPressEnter={() => {
                                    if (browseRows.length) pickSupplier(browseRows[0]);
                                    else messageApi.warning("No supplier to select");
                                }}
                                disabled={!browseCity}
                            />
                        </Fld>
                    </div>

                    {browseCity && !supplierLoading && (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                fontSize: 12,
                                color: "#64748b",
                                margin: "0 2px 6px",
                            }}
                        >
                            <span>
                                <strong style={{ color: "#1d4ed8" }}>{browseRows.length}</strong>{" "}
                                supplier(s) in{" "}
                                <strong style={{ color: "#0f172a" }}>{browseCity}</strong>
                            </span>
                            <span style={{ fontSize: 11 }}>Click a row to select</span>
                        </div>
                    )}

                    <div className="pi-sup-wrap">
                        <Table
                            className="pi-sup-table"
                            size="small"
                            columns={browseColumns}
                            dataSource={browseRows}
                            loading={supplierLoading}
                            rowKey={(_, i) => i}
                            pagination={{ pageSize: 8, size: "small", hideOnSinglePage: true }}
                            locale={{
                                emptyText: (
                                    <div
                                        style={{
                                            padding: "24px 0",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: 6,
                                            color: "#94a3b8",
                                        }}
                                    >
                                        <ShopOutlined style={{ fontSize: 28 }} />
                                        <span style={{ fontWeight: 600, fontSize: 13 }}>
                                            {browseCity
                                                ? "No suppliers found"
                                                : "Select a city to see its suppliers"}
                                        </span>
                                    </div>
                                ),
                            }}
                            onRow={(r) => ({
                                style: { cursor: "pointer" },
                                onClick: () => pickSupplier(r),
                            })}
                        />
                    </div>
                </div>
            </Modal>

            {/* ═════════════ SAVED PURCHASE INVOICES DIALOG – click a row to open it for editing ═════════════ */}
            <Modal
                title={null}
                open={listOpen}
                onCancel={() => setListOpen(false)}
                footer={null}
                width={980}
                destroyOnClose
                wrapClassName="pi-modal"
            >
                <DialogHead
                    icon={<FileSearchOutlined />}
                    title="Saved Purchase Invoices"
                    subtitle="Click an invoice to open it on the screen for editing"
                    gradient="linear-gradient(135deg,#4338ca 0%,#7c3aed 100%)"
                />
                <div className="pi-dlg-body" style={{ paddingBottom: 16 }}>
                    {/* filters: search + city + supplier + type + refresh */}
                    <div className="pi-list-filters">
                        <Fld label="Search" color="#1d4ed8">
                            <Input
                                allowClear
                                prefix={<SearchOutlined style={{ color: "#2563eb" }} />}
                                placeholder="Entry no / invoice no"
                                value={listText}
                                onChange={(e) => setListText(e.target.value)}
                            />
                        </Fld>
                        <Fld label="City" color="#1d4ed8">
                            <Sel
                                value={listCity}
                                onChange={(v) => {
                                    setListCity(v);
                                    setListSupplier(null); // supplier list depends on the city
                                }}
                                options={listOpts.city}
                                placeholder="All cities"
                            />
                        </Fld>
                        <Fld label="Supplier" color="#1d4ed8">
                            <Sel
                                value={listSupplier}
                                onChange={setListSupplier}
                                options={listOpts.supplier}
                                placeholder="All suppliers"
                            />
                        </Fld>
                        <Fld label="Type" color="#1d4ed8">
                            <Sel
                                value={listType}
                                onChange={setListType}
                                options={listOpts.type}
                                placeholder="All types"
                            />
                        </Fld>
                        <Button
                            icon={<ReloadOutlined />}
                            loading={listLoading}
                            onClick={refreshList}
                            style={{ height: 36, borderRadius: 8, fontWeight: 700 }}
                        >
                            Refresh
                        </Button>
                    </div>

                    {!listLoading && (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                fontSize: 12,
                                color: "#64748b",
                                margin: "0 2px 6px",
                            }}
                        >
                            <span>
                                <strong style={{ color: "#4338ca" }}>{filteredList.length}</strong>
                                {" of "}
                                {listRows.length} invoice(s)
                                {listFilterActive && (
                                    <a className="pi-clear-link" onClick={clearListFilters}>
                                        Clear filters
                                    </a>
                                )}
                            </span>
                            <span style={{ fontSize: 11 }}>Click a row to open</span>
                        </div>
                    )}

                    <div className="pi-sup-wrap">
                        <Table
                            className="pi-sup-table"
                            size="small"
                            columns={listColumns}
                            dataSource={filteredList}
                            loading={listLoading || openingNo !== null}
                            rowKey="entryNo"
                            pagination={{ pageSize: 8, size: "small", hideOnSinglePage: true }}
                            locale={{
                                emptyText: (
                                    <div
                                        style={{
                                            padding: "24px 0",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: 6,
                                            color: "#94a3b8",
                                        }}
                                    >
                                        <FileSearchOutlined style={{ fontSize: 28 }} />
                                        <span style={{ fontWeight: 600, fontSize: 13 }}>
                                            No saved purchase invoices found
                                        </span>
                                    </div>
                                ),
                            }}
                            onRow={(r) => ({
                                style: { cursor: "pointer" },
                                onClick: () => pickInvoice(r),
                            })}
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default PurchaseInvoice;